function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Asks Shopify to render one section instead of the whole page. A variant
// click, a filter or a search only swaps a few nodes, so fetching the full
// document meant downloading and parsing ~1 MB of HTML to use a fraction of it.
function sectionUrl(url, sectionId) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set("section_id", sectionId);
  return target.pathname + target.search;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

// localStorage throws where site data is blocked (some in-app browsers, strict
// privacy settings). In the cart drawer that threw out of a re-render halfway and
// left the checkout button locked, so storage there is a nice-to-have, never a step.
function safeStorage(method, ...args) {
  try {
    return localStorage[method](...args);
  } catch (error) {
    return null;
  }
}

function reportInputValidity(input, errorBlock) {
  if (!input || !errorBlock) return false;

  const type = input.type;
  const value = input.value.trim();
  let isValid = true;
  let errorType = "empty";

  isValid = value === "" ? false : true;

  switch (type) {
    case "email":
      if (value !== "" && !input.checkValidity()) {
        isValid = false;
        errorType = "email"
      }
      break;
  }

  if (isValid) {
    input.removeAttribute("aria-invalid");
    errorBlock.innerText = "";
  } else {
    input.setAttribute("aria-invalid", true);
    errorBlock.innerText = window.errorStrings[errorType];
  }

  return isValid;
}

window.errorStrings = window.errorStrings || {
  empty: "Please enter an email address.",
  email: "Please enter a valid email address.",
  subscribe: "Something went wrong. Please try again."
};

// properties: optional profile properties stored with the sign-up, e.g. the language of the
// launch sign-up (launch-signup.js).
async function subscribeToNewsletter(klaviyoId, listId, email, properties = null) {
  if (!klaviyoId || !listId || !email) return false;

  const profileAttributes = { email: email };
  if (properties) profileAttributes.properties = properties;

  const data = {
    method: "POST",
    headers: {
      "content-type": "application/json",
      revision: "2023-07-15"
    },
    body: JSON.stringify({
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: { type: "list", id: listId }
          }
        }
      }
    })
  };

  try {
    const link = `https://a.klaviyo.com/client/subscriptions/?company_id=${klaviyoId}`;
    return await fetch(link, data);
  } catch (error) {
    console.error(error.message);
    return false;
  }
}

function togglePageScroll() {
  if (document.documentElement.style.overflow == "hidden") {
    document.documentElement.style.overflow = null;
  } else {
    document.documentElement.style.overflow = "hidden";
  }
}

// Format cents using the shop's money format string (e.g. "€{{amount_with_comma_separator}}").
function formatMoney(cents, format) {
  if (typeof cents === "string") cents = cents.replace(".", "");
  cents = parseInt(cents, 10) || 0;

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const fmt = format || "${{amount}}";

  const withDelimiters = (precision, thousands = ",", decimal = ".") => {
    const value = (cents / 100).toFixed(precision);
    const parts = value.split(".");
    const integer = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1" + thousands);
    return parts[1] ? integer + decimal + parts[1] : integer;
  };

  let value;
  switch ((fmt.match(placeholder) || [])[1]) {
    case "amount_no_decimals": value = withDelimiters(0); break;
    case "amount_with_comma_separator": value = withDelimiters(2, ".", ","); break;
    case "amount_no_decimals_with_comma_separator": value = withDelimiters(0, ".", ","); break;
    case "amount_with_apostrophe_separator": value = withDelimiters(2, "'", "."); break;
    default: value = withDelimiters(2);
  }

  return fmt.replace(placeholder, value);
}

// --- SECTIONS --- //

class Header {
  constructor(root) {
    this.root = root;
    this.mobileMenu = this.root.querySelector("[data-drawer]");
    this.predictiveSearch = this.root.querySelector("#HeaderPredictiveSearch");
    this.allAnnouncementsHeight = 0;
    this.menuOpen = false;

    this.root.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));

    this.calcHeader();
    window.addEventListener('scroll', () => {
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
    });


    if (this.predictiveSearch) {
      this.root.querySelector("#HeaderSearchInput").addEventListener('input', debounce((e) => {
        this.onPredictiveChange(e.target.value);
      }, 500).bind(this));
    }
  }

  onPredictiveChange(query) {
    const predictiveSearchResults = this.root.querySelector("#HeaderSearchResults");

    // The last thing typed wins. Dropping a query while the previous one was in
    // flight left the results for what the customer typed before.
    this.searchRequest?.abort();

    if (query === "") {
      this.searchRequest = null;
      this.toggleLoading(false);
      predictiveSearchResults.innerHTML = "";
      return;
    }

    const request = new AbortController();
    this.searchRequest = request;
    this.toggleLoading(true);

    // Through the locale root, so /de-de gets German results and product links
    // that keep the visitor in the German market.
    const searchUrl = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query.trim())}&resources[type]=product,query&section_id=predictive-results`;

    fetch(searchUrl, { signal: request.signal })
    .then(response => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    })
    .then(response => {
      const html = new DOMParser().parseFromString(response, 'text/html');
      predictiveSearchResults.innerHTML = html.querySelector("#shopify-section-predictive-results").innerHTML;
    })
    .catch(error => {
      if (error.name === "AbortError") return;
      predictiveSearchResults.innerHTML = "";
      console.error(error);
    })
    .finally(() => {
      if (this.searchRequest === request) {
        this.searchRequest = null;
        this.toggleLoading(false);
      }
    })
  }

  calcHeader() {
		const headerObserver = new ResizeObserver(() => {
			this.headerHeight = this.root.getBoundingClientRect().height;

      const announcements = document.querySelectorAll(".announcement");
      this.allAnnouncementsHeight = Math.max(Array.from(announcements).reduce((sum, el) => {
        return sum + el.offsetHeight;
      }, 0), 1);
      
      document.documentElement.style.setProperty('--menu-height', `${this.headerHeight + this.allAnnouncementsHeight}px`);
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
      this.root.classList.toggle('sticky', window.scrollY >= this.allAnnouncementsHeight);
		});

		headerObserver.observe(this.root);
	}

  onClick(event) {
    if (!event.target.hasAttribute("data-action")) return;

    switch (event.target.dataset.action) {
      case "toggle-menu":
        this.toggleMobileMenu();
        break;
      case "toggle-search":
        if (!this.predictiveSearch.classList.contains("open")) {
          this.predictiveSearch.classList.add("open");
          document.documentElement.style.overflow = "hidden";
        } else {
          this.closePredictiveSearch();
        }
        break;
    }
  }

  onDocumentClick(event) {
    this.closeMobileMenuOnOutsideClick(event);

    if (!this.predictiveSearch || !this.predictiveSearch.classList.contains("open")) return;
    if (this.root.contains(event.target)) return;
    this.closePredictiveSearch();
  }

  closeMobileMenuOnOutsideClick(event) {
    if (!this.mobileMenu || !this.mobileMenu.classList.contains("open")) return;
    if (event.target.closest("[data-action='toggle-menu']")) return;
    if (event.target.closest(".header__drawer-inner")) return;
    this.closeMobileMenu();
  }

  closePredictiveSearch() {
    if (!this.predictiveSearch) return;
    this.predictiveSearch.classList.remove("open");
    document.documentElement.style.overflow = null;
  }

  toggleMobileMenu() {
    if (!this.mobileMenu.classList.contains("open")) {
      this.mobileMenu.classList.add("open");
      document.documentElement.style.overflow = "hidden";
    } else {
      this.closeMobileMenu();
    }
  }

  closeMobileMenu() {
    if (!this.mobileMenu) return;
    this.mobileMenu.classList.remove("open");
    document.documentElement.style.overflow = null;
  }

  toggleLoading(isLoading) {
    this.isLoading = isLoading;
    this.root.classList.toggle("loading", isLoading);
  }
}

if (document.querySelector("header")) {
  new Header(document.querySelector("header"))
}

// Animation functionality 

document.addEventListener('DOMContentLoaded', () => {
  initJsAnimations();
});

function initJsAnimations(userOpts = {}) {
  const opts = {
    sectionPxToTrigger: 150,
    cardThreshold: 0.2,
    sectionDuration: 0.7,
    cardDuration: 0.6,
    cardDelayStep: 0.3,
    rootMargin: '0px',
    once: true,
    ...userOpts
  };

  const sections = Array.from(document.querySelectorAll('[js-animation]'));
  if (!sections.length) return;

  sections.forEach(sec => {
    const sectionPxToTrigger = getNumber(sec.dataset.sectionPx, opts.sectionPxToTrigger);
    const sectionDuration = getNumber(sec.dataset.sectionDuration, opts.sectionDuration);
    const cardDuration = getNumber(sec.dataset.cardDuration, opts.cardDuration);
    const cardDelayStep = getNumber(sec.dataset.cardDelayStep, opts.cardDelayStep);

    sec.style.setProperty('--section-dur', `${sectionDuration}s`);
    sec.style.setProperty('--card-dur', `${cardDuration}s`);
    sec.style.setProperty('--card-delay-step', `${cardDelayStep}s`);

    // Збережемо опції секції
    sec.__animOpts = { sectionPxToTrigger, cardDelayStep };
  });

  // Спостерігач для карток лишаємо (threshold по картці як було)
  const cardObserver = new IntersectionObserver(onCardsIntersect, {
    root: null,
    rootMargin: opts.rootMargin,
    threshold: buildThresholds(opts.cardThreshold)
  });

  sections.forEach(sec => {
    getCards(sec).forEach(card => {
      const order = Number(card.getAttribute('js-animation-block')) || 0;
      card.__order = order;
      card.__animated = false;
      cardObserver.observe(card);
    });
  });

  const pending = new Set(sections);
  let ticking = false;

  function checkSections() {
    ticking = false;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    pending.forEach(sec => {
      const rect = sec.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= viewportH) {

        if (!opts.once) {
          sec.classList.remove('is-animated');
          getCards(sec).forEach(c => {
            c.__animated = false;
            c.classList.remove('is-animated');
            c.style.transitionDelay = '';
          });
        }
        return;
      }

      const needPx = Math.min(sec.__animOpts.sectionPxToTrigger, rect.height);
      const firstChunkVisible = rect.top <= (viewportH - needPx);

      if (firstChunkVisible) {
        if (!sec.classList.contains('is-animated')) {
          sec.classList.add('is-animated');
        }
        if (opts.once) pending.delete(sec);
      } else if (!opts.once) {
        sec.classList.remove('is-animated');
        getCards(sec).forEach(c => {
          c.__animated = false;
          c.classList.remove('is-animated');
          c.style.transitionDelay = '';
        });
      }
    });
  }

  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(checkSections);
    }
  }

  onScrollOrResize();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  let rafScheduled = false;
  const pendingVisibleBySection = new Map();

  function onCardsIntersect(entries) {
    for (const entry of entries) {
      const card = entry.target;
      const sec = findSection(card);
      if (!sec) continue;

      const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
      if (isVisible && !card.__animated) {
        if (!pendingVisibleBySection.has(sec)) pendingVisibleBySection.set(sec, new Set());
        pendingVisibleBySection.get(sec).add(card);
      }

      if (!isVisible && !opts.once && card.__animated) {
        card.__animated = false;
        card.classList.remove('is-animated');
        card.style.transitionDelay = '';
      }
    }

    if (!rafScheduled) {
      rafScheduled = true;
      requestAnimationFrame(flushVisibleBatches);
    }
  }

  function flushVisibleBatches() {
    rafScheduled = false;
    for (const [sec, setOfCards] of pendingVisibleBySection.entries()) {
      if (!sec.classList.contains('is-animated')) continue;

      const delayStep = sec.__animOpts?.cardDelayStep ?? opts.cardDelayStep;

      const batch = Array.from(setOfCards)
        .filter(c => !c.__animated)
        .sort((a, b) => (a.__order || 0) - (b.__order || 0));

      batch.forEach((card, idx) => {
        card.style.transitionDelay = `${idx * delayStep}s`;
        card.classList.add('is-animated');
        card.__animated = true;
      });

      setOfCards.clear();
    }
  }

  function getCards(sectionEl) {
    return Array.from(sectionEl.querySelectorAll('[js-animation-block]'));
  }

  function findSection(el) {
    return el.closest('[js-animation]');
  }

  function buildThresholds(minRatio) {
    const steps = 10;
    const arr = [];
    for (let i = 0; i <= steps; i++) arr.push(i / steps);
    if (!arr.includes(minRatio)) arr.push(minRatio);
    return arr.sort((a, b) => a - b);
  }

  function getNumber(val, fallback) {
    const n = Number(val);
    return Number.isFinite(n) ? n : fallback;
  }
}

class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.dataset.type == "recommendations") {
      this.setRecommendationProducts();
    }
  }

  setRecommendationProducts() {
    const recommendationLink = window.Shopify.routes.root + `recommendations/products?limit=4&product_id=${this.dataset.product}&intent=${this.dataset.recommendations}&section_id=${this.dataset.section}`;
      fetch(recommendationLink)
      .then(response => response.text())
      .then(response => {
        const html = new DOMParser().parseFromString(response, "text/html");

        if (html.querySelector("featured-products[data-empty]")) {
          this.style.display = "none";
          return;
        }
        
        const grid = this.querySelector('[id^="Slider-"]');
        grid.innerHTML = html.querySelector('[id^="Slider-"]').innerHTML;

        this.querySelector("slider-component").update();
      })
      .catch(error => console.error(error))
  }
}

if (document.querySelector("featured-products")) {
  customElements.define("featured-products", FeaturedProduct);
}

class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.cart = document.querySelector("cart-drawer");
    this.productVariants = JSON.parse(this.querySelector("[data-variants-data]").textContent);
    this.updateGallery = this.updateGallery.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onSubmitPress = this.onSubmitPress.bind(this);
    this.onUgcClick = this.onUgcClick.bind(this);
    this.onUgcKeydown = this.onUgcKeydown.bind(this);
    this.onBundleClick = this.onBundleClick.bind(this);
    this.onBundleKeydown = this.onBundleKeydown.bind(this);
    this.scheduleStickyUpdate = this.scheduleStickyUpdate.bind(this);
    this.onStickyResize = this.onStickyResize.bind(this);
  }
  
  connectedCallback() {
    this.productGallery = this.querySelector('[id^="ProductGallery-"]');
    this.productOptions = this.querySelector('[id^="ProductOptions-"]');
    this.productForm = this.querySelector('[id^="ProductForm-"]');
    
    this.productGallery?.addEventListener('slider-updated', this.updateGallery);
    this.productOptions?.addEventListener("change", this.onVariantChange);
    this.productForm?.addEventListener("submit", this.onFormSubmit);
    this.addEventListener("pointerdown", this.onSubmitPress);

    this.initClickListeners();

    if (this.querySelector('[id^="ProductSticky-"]')) {
      this.initStickyBar();
    }

    this.initUgc();
    this.initBundle();
  }

  disconnectedCallback() {
    this.productGallery?.removeEventListener('slider-updated', this.updateGallery);
    this.productOptions?.removeEventListener("change", this.onVariantChange);
    this.productForm?.removeEventListener("submit", this.onFormSubmit);
    this.removeEventListener("pointerdown", this.onSubmitPress);
    document.removeEventListener("keydown", this.onUgcKeydown);
    document.removeEventListener("keydown", this.onBundleKeydown);

    if (this.stickyBar) {
      window.removeEventListener("scroll", this.scheduleStickyUpdate);
      window.removeEventListener("resize", this.onStickyResize);
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      document.body.style.paddingBottom = "";
    }
  }

  onFormSubmit(event = false) {
    if (event) event.preventDefault();

    if (this.isLoading || this.checkingGift) return;

    // Scope the loader to the button that actually submitted (main add-to-cart
    // or the sticky bar) instead of the whole section.
    const loadingEl = (event && event.submitter) || null;

    const giftVariant = this.dataset.giftVariant;

    // No gift configured for this product — add as usual
    if (!giftVariant) {
      this.submitCart(null, loadingEl);
      return;
    }

    // The drawer is rendered from the cart and re-rendered on every change, so
    // it already knows whether a gift is in there. Reading it costs nothing and
    // takes a whole round trip out of the click; the cart.js read below is kept
    // for pages that carry no drawer.
    if (this.cart) {
      this.submitCart(this.cart.hasAnyGift() ? null : giftVariant, loadingEl);
      return;
    }

    // Free gift: limit 1 per order — only add the gift if none is present yet.
    // That check is a round trip to the cart, so the loader goes on now rather
    // than after it: the button used to sit silent for the whole check.
    this.checkingGift = true;
    (loadingEl || this).classList.add("loading");

    this.takeGiftCheck()
      .then(hasGift => {
        this.checkingGift = false;

        // A variant switch started while the check ran and owns the section
        // now; the add is dropped exactly as before, just without a stuck loader.
        if (this.isLoading) {
          (loadingEl || this).classList.remove("loading");
          return;
        }

        this.submitCart(hasGift ? null : giftVariant, loadingEl);
      })
      .catch(error => {
        this.checkingGift = false;
        (loadingEl || this).classList.remove("loading");
        this.showError(error);
      });
  }

  // The gift check reads the live cart. Pressing the add button starts it a
  // beat before the submit that follows, so the add waits on less of it.
  // Only for pages without a drawer — where there is one, onFormSubmit reads
  // the answer off it and this request would never be collected.
  onSubmitPress(event) {
    if (!this.dataset.giftVariant || this.cart) return;

    // An add still in flight is about to change the cart, so a check read now
    // could miss the gift it brings and let a second one through.
    if (this.isLoading || this.checkingGift) return;

    const button = event.target.closest('button[type="submit"]');
    if (!button || button.form !== this.productForm) return;

    this.giftCheck = { startedAt: Date.now(), result: this.fetchHasGift() };
  }

  takeGiftCheck() {
    const pending = this.giftCheck;
    this.giftCheck = null;

    // Only a press from moments ago still reflects the cart.
    if (pending && Date.now() - pending.startedAt < 3000) return pending.result;

    return this.fetchHasGift();
  }

  fetchHasGift() {
    return fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => cart.items.some(item => item.properties && item.properties._gift))
      .catch(() => false);
  }

  submitCart(giftVariant, loadingEl = null) {
    if (this.isLoading) return;

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const items = [
      {
        id: parseInt(this.querySelector("[name='id']").value),
        quantity: parseInt(this.querySelector("[name='quantity']").value)
      }
    ];

    // Bind the main product and its free gift with a shared timestamp group,
    // so they can be removed together from the cart drawer.
    if (giftVariant) {
      const groupId = Date.now().toString();
      items[0].properties = { _gift_group: groupId };
      items.push({
        id: parseInt(giftVariant),
        quantity: 1,
        properties: { _gift_group: groupId, _gift: 'true' }
      });
    }

    // Accessory the customer switched on. It rides this request instead of
    // getting its own button, but stays an ordinary line: it is paid for, so the
    // cart has to show its price rather than treat it as a gift.
    const crossSell = this.querySelector("[data-cross-sell-input]");
    if (crossSell?.checked && crossSell.dataset.variant) {
      items.push({
        id: parseInt(crossSell.dataset.variant),
        quantity: 1
      });
    }

    // The order gift rides along instead of costing its own request and its own
    // re-render after this one lands.
    if (this.cart) items.push(...this.cart.addOnItems());

    this.addToCart({
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    }, loadingEl);
  }

  addToCart(data, loadingEl = null) {
    // toggleLoading flips the flag, so a second add over one in flight would turn
    // it off while both requests are still running.
    if (!data || this.isLoading) return;

    this.toggleLoading(false, loadingEl);

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading(false, loadingEl);

        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => {
      // The cart just changed, so a gift check taken before this add is stale.
      this.giftCheck = null;
      this.toggleLoading(true, loadingEl);

      // A variant picked while the add was in flight was held back so it would not
      // swap the button out from under its spinner.
      if (this.pendingVariantHtml) {
        const html = this.pendingVariantHtml;
        this.pendingVariantHtml = null;
        this.renderContent(html);
      }
      if (this.variantRequest) this.classList.add("loading");
    });
  }

  // Never blocked by an add in flight: the radio has already moved, so ignoring the
  // change left the form adding the variant the customer had just switched away from.
  // The variant id is set straight away; only the re-render waits for the add.
  onVariantChange() {
    const searchParams = new URLSearchParams();
    let selectedValuesLabel = '';

    [1, 2, 3].forEach(option => {
      if (this.querySelector(`[data-option="${option}"]`)) {
        if (this.querySelector(`[data-option="${option}"] input:checked`)) {
          selectedValuesLabel += ' / ' + this.querySelector(`[data-option="${option}"] input:checked`).value;
        } else {
          selectedValuesLabel += ' / ' + this.querySelector(`[data-option="${option}"] input`).value;
        }
      }
    });

    const variant = this.productVariants.find(variant => variant.title === selectedValuesLabel.slice(3));
    if (variant) {
      if (this.querySelector("input[name='id']")) {
        this.querySelector("input[name='id']").value = variant.id;
      }
      searchParams.set('variant', variant.id);

      // Sold out is known now; the re-render only confirms it.
      this.querySelectorAll('[id^="ProductSubmit-"] button[type="submit"], [id^="ProductSticky-"] button[type="submit"]').forEach(button => {
        button.disabled = !variant.available;
      });
    }

    const newProductUrl = window.location.origin + window.location.pathname + `?${searchParams.toString()}`;
    window.history.replaceState({}, '', newProductUrl);

    // Only the last pick renders; a slower answer for an earlier one is dropped.
    this.variantRequest?.abort();
    const request = new AbortController();
    this.variantRequest = request;
    this.classList.add("loading");

    fetch(sectionUrl(newProductUrl, this.dataset.section), { signal: request.signal })
    .then(res => res.text())
    .then(body => {
      const html = new DOMParser().parseFromString(body, "text/html");

      if (this.isLoading) {
        this.pendingVariantHtml = html;
      } else {
        this.renderContent(html);
      }
    })
    .catch(error => {
      if (error.name !== "AbortError") this.showError(error);
    })
    .finally(() => {
      if (this.variantRequest !== request) return;
      this.variantRequest = null;
      if (!this.isLoading) this.classList.remove("loading");
    })
  }

  renderContent(html) {
    const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
    const selectors = [
      '[id^="ProductPrice-"]',
      '[id^="ProductOptions-"]',
      '[id^="ProductSticky-"]',
      '[id^="ProductSubmit-"]'
    ];

    selectors.forEach(selector => {
      const currentSelectors = this.querySelectorAll(selector);
      const newSelectors = section.querySelectorAll(selector);

      if (currentSelectors.length > 0 && currentSelectors.length == newSelectors.length) {
        currentSelectors.forEach((currentSelector, i) => {
          currentSelector.innerHTML = newSelectors[i].innerHTML;
        })
      }
    });

    if (this.querySelector('[id^="ProductSubmit-"]')) {
      const currentButton = this.querySelector('[id^="ProductSubmit-"]');
      const newButton = section.querySelector('[id^="ProductSubmit-"]');
      currentButton.toggleAttribute("disabled", newButton.disabled);
    }

    // The form adds whatever the section now shows. For a combination that does not
    // exist the server falls back to another variant, and the id has to follow it.
    const currentId = this.querySelector("input[name='id']");
    const newId = section.querySelector("input[name='id']");
    if (currentId && newId) currentId.value = newId.value;
  }

  initClickListeners() {
    this.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-action")) {
        const productGalleryComponent = this.productGallery.querySelector("slider-component");

        switch (e.target.dataset.action) {
          case "select-slide":
            productGalleryComponent.scrollToSlide(e.target.dataset.index);
            break;
          case "toggle-disclosure":
            const disclosureHandle = e.target.dataset.disclosure;
            const disclosureContent = this.querySelector(`[data-disclosure-content="${disclosureHandle}"]`);
              
            e.target.classList.toggle("open");

            if (e.target.classList.contains("open")) {
              disclosureContent.style.maxHeight = disclosureContent.scrollHeight + "px";
            } else {
              disclosureContent.style.maxHeight = null;
            }
            break;
          case "add-upsell":
            if (this.isLoading) break;
            const cartData = {
              items: [
                {
                  id: parseInt(e.target.dataset.variant),
                  quantity: 1
                }
              ],
              sections_url: window.location.pathname
            };

            if (this.cart) {
              cartData["sections"] = ["cart-drawer"];
            };

            this.addToCart(cartData, e.target.closest('[data-action="add-upsell"]'));
            break;
        }
      }
    })
  }

  initStickyBar() {
    // Both nodes survive a variant change: renderContent() swaps their
    // innerHTML but keeps the elements, so they can be looked up once instead
    // of on every scroll event.
    this.stickyBar = this.querySelector('[id^="ProductSticky-"]');
    this.productSubmit = this.querySelector('[id^="ProductSubmit-"]');
    this.stickyFrame = null;
    this.stickyBarHeight = 0;

    this.measureStickyBar();
    this.updateStickyBar();

    window.addEventListener("scroll", this.scheduleStickyUpdate, { passive: true });
    window.addEventListener("resize", this.onStickyResize, { passive: true });
  }

  // At most one measurement per frame, so a fast scroll cannot queue up work.
  scheduleStickyUpdate() {
    if (this.stickyFrame) return;

    this.stickyFrame = requestAnimationFrame(() => {
      this.stickyFrame = null;
      this.updateStickyBar();
    });
  }

  onStickyResize() {
    this.measureStickyBar();
    this.scheduleStickyUpdate();
  }

  // The bar sits over the end of the page, so the body reserves its height.
  // Set once per viewport size rather than per toggle: adding and removing the
  // space on every show/hide would shift the page and feed back into the
  // measurement below.
  measureStickyBar() {
    if (!this.stickyBar) return;

    this.stickyBarHeight = this.stickyBar.offsetHeight;
    document.body.style.paddingBottom = this.stickyBarHeight
      ? `${this.stickyBarHeight}px`
      : "";
  }

  updateStickyBar() {
    if (!this.stickyBar) return;

    // Desktop never shows the bar — the buy zone is above the fold there.
    const show = window.innerWidth <= 989 && !this.isSubmitInViewport();
    this.stickyBar.classList.toggle("show", show);
  }

  // The bar stands in for the real add-to-cart button while it is out of
  // reach, so the two must never be on screen together. The bar covers the
  // bottom strip, which means the button only counts as reachable above the
  // bar's top edge — a button sitting behind the bar is not usable.
  isSubmitInViewport() {
    if (!this.productSubmit) return false;

    const rect = this.productSubmit.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Hiding happens exactly at the bar's top edge, so no scroll position shows
    // both buttons. Coming back waits another 26px, which stops a jittery
    // viewport — iOS collapsing its address bar — from flapping the bar.
    const shown = this.stickyBar.classList.contains("show");
    const barEdge = viewportHeight - this.stickyBarHeight + (shown ? 0 : 26);

    return rect.bottom > 0 && rect.top < barEdge;
  }

  updateGallery(event) {
    const nav = this.querySelector('[id^="Slider-GalleryNav-"]');

    // The highlighted thumb only changes when the slide does, so keep this out
    // of the per-scroll path.
    if (event.detail.currentSlide !== this.activeSlide) {
      this.activeSlide = event.detail.currentSlide;
      this.querySelectorAll('[id^="Slider-GalleryNav-"] li').forEach(slide => {
        slide.classList.toggle("active", parseInt(slide.dataset.index) === this.activeSlide);
      });
    }

    if (!nav) return;

    const maxScroll = nav.scrollWidth - nav.clientWidth;
    if (maxScroll <= 0) return;

    // Follow the gallery position directly instead of animating the strip
    // towards the active thumb: the animation restarted on every scroll event
    // and left the thumbs trailing behind the swipe.
    const left = event.detail.progress * maxScroll;

    if (this.navScrollFrame) cancelAnimationFrame(this.navScrollFrame);
    this.navScrollFrame = requestAnimationFrame(() => {
      nav.scrollLeft = left;
      this.navScrollFrame = null;
    });
  }

  showError(error) {
    console.error(error);

    if (!this.querySelector("[product-notification]")) return;

    const messageBlock = this.querySelector("[product-notification]");

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ''}, 5000);
    messageBlock.scrollIntoView({behavior: "smooth",block:"center"});
  }

  toggleLoading(turnOff = false, el = null) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    (el || this).classList.toggle("loading", this.isLoading);
  }

  // --- UGC stories ---
  initUgc() {
    this.ugcPopup = this.querySelector('[id^="ProductUgc-"]');
    this.ugcPreviews = this.querySelectorAll(".main-product__ugc-thumb-video");

    if (this.ugcPopup) {
      this.ugcSlides = Array.from(this.ugcPopup.querySelectorAll(".main-product__ugc-slide"));
      this.ugcBars = Array.from(this.ugcPopup.querySelectorAll(".main-product__ugc-bar"));
      this.ugcPrev = this.ugcPopup.querySelector(".main-product__ugc-nav--prev");
      this.ugcNext = this.ugcPopup.querySelector(".main-product__ugc-nav--next");
      this.ugcIndex = 0;
      this.ugcMuted = false;

      this.ugcSlides.forEach((slide) => {
        const video = slide.querySelector("video");

        video.addEventListener("timeupdate", () => {
          if (!slide.classList.contains("active") || !video.duration) return;
          const bar = this.ugcBars[this.ugcIndex];
          if (bar) bar.querySelector("span").style.width = `${(video.currentTime / video.duration) * 100}%`;
        });

        video.addEventListener("ended", () => {
          const bar = this.ugcBars[this.ugcIndex];
          if (bar) bar.classList.add("watched");
          this.changeUgc(this.ugcIndex + 1);
        });

        video.addEventListener("play", () => {
          if (slide.classList.contains("active")) this.ugcPopup.classList.remove("is-paused");
        });

        video.addEventListener("pause", () => {
          if (slide.classList.contains("active") && !video.ended) this.ugcPopup.classList.add("is-paused");
        });
      });

      this.addEventListener("click", this.onUgcClick);
      document.addEventListener("keydown", this.onUgcKeydown);
    }

    if (this.ugcPreviews.length && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.play().catch(() => {});
          else entry.target.pause();
        });
      }, { rootMargin: "100px" });

      this.ugcPreviews.forEach((video) => observer.observe(video));
    }
  }

  onUgcClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case "open-ugc":
        this.openUgc(parseInt(trigger.dataset.index, 10) || 0);
        break;
      case "close-ugc":
        if (trigger === this.ugcPopup || trigger.classList.contains("main-product__ugc-close")) this.closeUgc();
        break;
      case "prev-ugc":
        this.changeUgc(this.ugcIndex - 1);
        break;
      case "next-ugc":
        this.changeUgc(this.ugcIndex + 1);
        break;
      case "toggle-ugc-sound":
        this.toggleUgcSound();
        break;
      case "toggle-ugc-play":
        this.toggleUgcPlay();
        break;
    }
  }

  onUgcKeydown(event) {
    if (!this.ugcPopup || !this.ugcPopup.classList.contains("open")) return;
    if (event.key === "Escape") this.closeUgc();
    else if (event.key === "ArrowRight") this.changeUgc(this.ugcIndex + 1);
    else if (event.key === "ArrowLeft") this.changeUgc(this.ugcIndex - 1);
  }

  setUgcBar(bar, width) {
    bar.classList.add("no-transition");
    bar.querySelector("span").style.width = width;
    void bar.offsetWidth;
    bar.classList.remove("no-transition");
  }

  changeUgc(next) {
    if (next < 0) return;
    if (next >= this.ugcSlides.length) return this.closeUgc();

    const prevSlide = this.ugcSlides[this.ugcIndex];
    if (prevSlide) {
      const prevVideo = prevSlide.querySelector("video");
      prevVideo.pause();
      prevVideo.currentTime = 0;
      prevSlide.classList.remove("active");
    }

    this.ugcIndex = next;

    this.ugcBars.forEach((bar, i) => {
      bar.classList.toggle("watched", i < this.ugcIndex);
      this.setUgcBar(bar, i < this.ugcIndex ? "100%" : "0%");
    });

    if (this.ugcPrev) this.ugcPrev.classList.toggle("is-disabled", this.ugcIndex === 0);
    if (this.ugcNext) this.ugcNext.classList.toggle("is-disabled", this.ugcIndex === this.ugcSlides.length - 1);

    const slide = this.ugcSlides[this.ugcIndex];
    const video = slide.querySelector("video");
    slide.classList.add("active");
    video.muted = this.ugcMuted;
    video.currentTime = 0;
    video.play().catch(() => {});
  }

  openUgc(index) {
    if (!this.ugcPopup) return;
    this.ugcPopup.classList.add("open");
    this.ugcPopup.classList.toggle("muted", this.ugcMuted);
    document.documentElement.style.overflow = "hidden";
    this.changeUgc(index);
  }

  closeUgc() {
    if (!this.ugcPopup) return;
    this.ugcPopup.classList.remove("open");
    this.ugcPopup.classList.remove("is-paused");
    document.documentElement.style.overflow = "";

    this.ugcSlides.forEach((slide) => {
      const video = slide.querySelector("video");
      video.pause();
      video.currentTime = 0;
      slide.classList.remove("active");
    });

    this.ugcBars.forEach((bar) => {
      bar.classList.remove("watched");
      this.setUgcBar(bar, "0%");
    });
  }

  toggleUgcSound() {
    if (!this.ugcPopup) return;
    this.ugcMuted = !this.ugcMuted;
    this.ugcPopup.classList.toggle("muted", this.ugcMuted);
    const slide = this.ugcSlides[this.ugcIndex];
    if (slide) slide.querySelector("video").muted = this.ugcMuted;
  }

  toggleUgcPlay() {
    const slide = this.ugcSlides[this.ugcIndex];
    if (!slide) return;
    const video = slide.querySelector("video");
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  // --- Bundle contents popup ---
  initBundle() {
    this.bundlePopup = this.querySelector('[id^="ProductBundle-"]');
    if (!this.bundlePopup) return;

    this.addEventListener("click", this.onBundleClick);
    document.addEventListener("keydown", this.onBundleKeydown);
  }

  onBundleClick(event) {
    const trigger = event.target.closest("[data-action]");
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case "open-bundle":
        this.openBundle(parseInt(trigger.dataset.index, 10) || 0);
        break;
      case "select-bundle":
        this.selectBundle(parseInt(trigger.dataset.index, 10) || 0);
        break;
      case "close-bundle":
        // Clicks inside the card bubble up to the overlay, which carries the same
        // action; only the overlay itself, or a button, closes it. The add-to-cart
        // button closes it too, so the cart drawer it opens is not covered.
        if (trigger !== this.bundlePopup || event.target === this.bundlePopup) this.closeBundle();
        break;
    }
  }

  onBundleKeydown(event) {
    if (event.key === "Escape" && this.bundlePopup.classList.contains("open")) this.closeBundle();
  }

  openBundle(index) {
    this.selectBundle(index);
    this.bundlePopup.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  closeBundle() {
    this.bundlePopup.classList.remove("open");
    document.documentElement.style.overflow = "";
  }

  selectBundle(index) {
    this.bundlePopup.querySelectorAll("[data-bundle-slide]").forEach((el) => {
      el.classList.toggle("active", el.dataset.bundleSlide === String(index));
    });
    this.bundlePopup.querySelectorAll("[data-bundle-detail]").forEach((el) => {
      el.classList.toggle("active", el.dataset.bundleDetail === String(index));
    });
    this.bundlePopup.querySelectorAll(".main-product__bundle-popup-thumb").forEach((el) => {
      el.classList.toggle("active", el.dataset.index === String(index));
    });
  }
}

if (document.querySelector("main-product")) {
  customElements.define("main-product", MainProduct);
}

class MainCollection extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.filtersWrapper = this.querySelector("[data-filters]");
    this.productsGrid = this.querySelector('[id^="CollectionGrid-"]');

    this.initClickEvents();
    if (this.filtersWrapper) this.initChangeEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      if (!e.target.hasAttribute("data-action")) return;

      switch (e.target.dataset.action) {
        case "toggle-filter": {
          const filter = e.target.closest("[data-filter]");
          if (filter) {
            const isOpen = filter.classList.toggle("open");
            e.target.setAttribute("aria-expanded", isOpen);
          }
          break;
        }
        case "open-options":
          this.querySelector("[data-sort]")?.classList.toggle("open");
          break;
        case "select-option": {
          this.querySelector("[data-sort]")?.classList.remove("open");
          const sortInput = this.querySelector("[name='sort_by']");
          if (sortInput) sortInput.value = e.target.dataset.option;
          this.requestFiltersUpdate();
          break;
        }
        case "remove-filter":
          if (!this.isLoading) this.filtersUpdate(e.target.dataset.url);
          break;
        case "show-more":
          if (!this.isLoading) this.showMore(e.target);
          break;
      }
    })

    window.addEventListener("click", (e) => {
      if (!e.target.closest("[data-sort]")) {
        this.querySelector("[data-sort]")?.classList.remove("open");
      }
    })
  }

  initChangeEvents() {
    this.filtersWrapper.addEventListener("change", () => this.requestFiltersUpdate());

    this.filtersWrapper.addEventListener("submit", (e) => e.preventDefault());
  }

  // A box ticked while the grid is loading is already ticked on screen, so dropping
  // the change left results that did not match the filters. It runs once the
  // current request is done, from whatever the inputs say by then.
  requestFiltersUpdate() {
    if (this.isLoading) {
      this.filtersPending = true;
      return;
    }
    this.filtersUpdate();
  }

  filtersUpdate(urlToRemove = false) {
    this.toggleLoading();

    let newProductUrl = urlToRemove;

    if (!newProductUrl) {
      const searchParams = new URLSearchParams();
      const sortInput = this.querySelector("[name='sort_by']");
      if (sortInput) searchParams.append("sort_by", sortInput.value);

      if (this.filtersWrapper) {
        this.filtersWrapper.querySelectorAll("input:checked").forEach((input) => {
          searchParams.append(input.name, input.value);
        })

        this.filtersWrapper.querySelectorAll("[data-price] input").forEach((input) => {
          if (input.value !== "") searchParams.append(input.name, input.value);
        })
      }

      newProductUrl = window.location.pathname + `?${searchParams.toString()}`;
    }

    window.history.replaceState({}, "", newProductUrl);

    fetch(sectionUrl(newProductUrl, this.dataset.section))
    .then((res) => res.text())
    .then((body) => {
      const html = new DOMParser().parseFromString(body, "text/html");
      const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
      if (section) this.renderContent(section);
    })
    .catch((error) => console.error(error))
    .finally(() => this.toggleLoading())
  }

  // Every click reveals one step of cards. Held-back cards go first; when fewer than a step
  // are left, the next page is fetched and appended hidden, so the step stays the same.
  showMore(button) {
    const step = parseInt(button.dataset.step, 10) || 8;
    const hiddenCount = this.querySelectorAll("[data-more].hidden").length;

    if (hiddenCount >= step || !button.dataset.url) {
      this.revealCards(button, step);
      return;
    }

    this.toggleLoading();

    fetch(sectionUrl(button.dataset.url, this.dataset.section))
    .then((res) => res.text())
    .then((body) => {
      const html = new DOMParser().parseFromString(body, "text/html");
      const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
      if (!section) return;

      const grid = this.querySelector("[data-grid]");
      section.querySelectorAll("[data-grid] > .main-collection__card").forEach((card) => {
        card.classList.add("hidden");
        card.setAttribute("data-more", "");
        grid.append(card);
      });

      const nextUrl = section.querySelector('[data-action="show-more"]')?.dataset.url;
      if (nextUrl) {
        button.dataset.url = nextUrl;
      } else {
        delete button.dataset.url;
      }

      this.revealCards(button, step);
    })
    .catch((error) => console.error(error))
    .finally(() => this.toggleLoading())
  }

  revealCards(button, step) {
    const hiddenCards = Array.from(this.querySelectorAll("[data-more].hidden"));
    hiddenCards.slice(0, step).forEach((card) => card.classList.remove("hidden"));
    if (hiddenCards.length <= step && !button.dataset.url) button.remove();
  }

  renderContent(section) {
    // Swap each filter's values in place (keyed by data-index) so an open filter dropdown stays open.
    this.querySelectorAll("[data-filter]").forEach((filter) => {
      const newFilter = section.querySelector(`[data-filter][data-index="${filter.dataset.index}"]`);
      const currentValues = filter.querySelector("[data-values]");
      const newValues = newFilter ? newFilter.querySelector("[data-values]") : null;
      if (currentValues && newValues) currentValues.innerHTML = newValues.innerHTML;
    });

    // Replace the results and the sort state without touching the toolbar wrappers.
    // The semicolon above matters: without it the array below is read as an
    // index on forEach's undefined result, which threw and left the grid stale.
    ["[data-grid]", "[data-pagination]", "[data-sort-current]", "[data-sort-options]"].forEach((selector) => {
      const current = this.querySelector(selector);
      const next = section.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    })
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);

    if (!this.isLoading && this.filtersPending) {
      this.filtersPending = false;
      this.filtersUpdate();
    }
  }
}

if (document.querySelector("main-collection")) {
  customElements.define("main-collection", MainCollection);
}

class MainSearch extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.form = this.querySelector("[data-form]");

    if (this.form) this.form.addEventListener("submit", this.onSubmit.bind(this));
  }

  onSubmit(event) {
    event.preventDefault();
    if (!this.isLoading) this.search();
  }

  search() {
    this.toggleLoading();

    const searchParams = new URLSearchParams(new FormData(this.form));
    const newSearchUrl = `${this.form.getAttribute("action")}?${searchParams.toString()}`;

    window.history.replaceState({}, "", newSearchUrl);

    fetch(sectionUrl(newSearchUrl, this.dataset.section))
    .then((res) => res.text())
    .then((body) => {
      const html = new DOMParser().parseFromString(body, "text/html");
      const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
      if (section) this.renderContent(section);
    })
    .catch((error) => console.error(error))
    .finally(() => this.toggleLoading())
  }

  renderContent(section) {
    ["[data-count]", "[data-grid]", "[data-pagination]"].forEach((selector) => {
      const current = this.querySelector(selector);
      const next = section.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    })
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-search")) {
  customElements.define("main-search", MainSearch);
}

class MainBlog extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.initClickEvents();
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-action]");
      if (!trigger || this.isLoading) return;

      switch (trigger.dataset.action) {
        case "switch-blog":
          if (trigger.classList.contains("active")) break;
          this.querySelector("[data-tabs] .active")?.classList.remove("active");
          trigger.classList.add("active");
          this.switchBlog(trigger.dataset.blog);
          break;
        case "load-more":
          this.loadMore(trigger.dataset.url);
          break;
      }
    });
  }

  // Blog pills: fetch the target blog page and swap the header, the grid and the load more button,
  // so the load more chain restarts from page 1 of the newly selected blog.
  switchBlog(handle) {
    if (!handle) return;

    const newBlogUrl = `${window.Shopify.routes.root}blogs/${handle}`;

    window.history.replaceState({}, "", newBlogUrl);

    this.fetchSection(newBlogUrl, (section) => {
      ["[data-header]", "[data-grid]", "[data-pagination]"].forEach((selector) => {
        const current = this.querySelector(selector);
        const next = section.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      })
    });
  }

  // Load more: append the next page's cards and hand the button the following page url.
  loadMore(url) {
    if (!url) return;

    this.fetchSection(url, (section) => {
      const grid = this.querySelector("[data-grid]");
      const nextGrid = section.querySelector("[data-grid]");
      if (grid && nextGrid) grid.insertAdjacentHTML("beforeend", nextGrid.innerHTML);

      const pagination = this.querySelector("[data-pagination]");
      const nextPagination = section.querySelector("[data-pagination]");
      if (pagination && nextPagination) pagination.innerHTML = nextPagination.innerHTML;
    });
  }

  fetchSection(url, onSection) {
    this.toggleLoading();

    fetch(sectionUrl(url, this.dataset.section))
    .then((res) => res.text())
    .then((body) => {
      const html = new DOMParser().parseFromString(body, "text/html");
      const section = html.querySelector(`[data-section="${this.dataset.section}"]`);
      if (section) onSection(section);
    })
    .catch((error) => console.error(error))
    .finally(() => this.toggleLoading());
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("main-blog")) {
  customElements.define("main-blog", MainBlog);
}

class MainArticle extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector("[data-content]");
    this.list = this.querySelector("[data-toc]");
    this.aside = this.querySelector("[data-aside]");

    if (this.content && this.list) this.buildToc();
  }

  // The table of contents is derived from the headings the copywriter put in the article body,
  // so the aside stays hidden until there is at least one heading to link to.
  buildToc() {
    this.headings = Array.from(this.content.querySelectorAll("h1, h2, h3")).filter((heading) => heading.textContent.trim());
    if (!this.headings.length) return;

    this.headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `article-heading-${index + 1}`;

      const item = document.createElement("li");
      item.className = "main-article__toc-item";

      const link = document.createElement("a");
      link.className = `main-article__toc-link main-article__toc-link--${heading.tagName.toLowerCase()}`;
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      item.appendChild(link);
      this.list.appendChild(item);
    });

    this.links = Array.from(this.list.querySelectorAll(".main-article__toc-link"));
    if (this.aside) this.aside.hidden = false;

    this.list.addEventListener("click", this.onClick.bind(this));
    this.initObserver();
  }

  onClick(e) {
    const link = e.target.closest(".main-article__toc-link");
    if (!link) return;

    const target = document.getElementById(link.getAttribute("href").slice(1));
    if (!target) return;

    e.preventDefault();

    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - headerHeight - 24,
      behavior: "smooth"
    });
  }

  initObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.setActive(entry.target.id);
      })
    }, { rootMargin: "0px 0px -70% 0px" });

    this.headings.forEach((heading) => observer.observe(heading));
  }

  setActive(id) {
    this.links.forEach((link) => link.classList.toggle("active", link.getAttribute("href") === `#${id}`));
  }
}

if (document.querySelector("main-article")) {
  customElements.define("main-article", MainArticle);
}

// --- COMPONENTS --- //

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.currentSlide = 1;
    this.allowEvents = this.getAttribute("data-allow-events") === "true";
    this.hideControls = this.getAttribute("data-hide-controls") !== "false"

    this.slider = this.querySelector('[id^="Slider-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.scrollbar = this.querySelector("[slider-scrollbar]");
    this.scrollbarThumb = this.querySelector("[slider-scrollbar] span");

    this.pagination = this.querySelector('[slider-pagination]');

    this.counter = this.querySelector('[slider-counter]');

    if (!this.slider) return;

    this.update();
    window.addEventListener('resize', this.update.bind(this));
    this.slider.addEventListener('scroll', this.update.bind(this));

    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    if (this.pagination) {
      this.pagination.addEventListener("click", this.onDotClick.bind(this))
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (event.currentTarget.name === 'previous' && this.currentSlide > 1) {
      this.currentSlide -= 1;
    } else if (event.currentTarget.name === 'next' && this.currentSlide < this.maxLength) {
      this.currentSlide += 1;
    }
    this.scrollToSlide(this.currentSlide);
  }

  onDotClick(event) {
    event.preventDefault();
    if (!event.target.hasAttribute("data-slide")) return;

    const slideIndex = parseInt(event.target.dataset.slide);
    this.scrollToSlide(slideIndex);
  }

  scrollToSlide(index) {
    this.slider.scrollTo({
      left: this.querySelector(`[id^="Slide-"]:nth-child(${index})`).offsetLeft,
      behavior: 'smooth'
    });
  }

  update() {
    const items = this.querySelectorAll('[id^="Slide-"]');
    const scrollLeft = this.slider.scrollLeft;
    const scrollWidth = this.slider.scrollWidth;
    const clientWidth = this.slider.clientWidth;
    const blockWidth = Array.from(items).find(item => item.offsetWidth > 0)?.offsetWidth ?? 0;
    const gap = window.innerWidth < 750 ? 12 : 16;
    const hasScroll = this.slider.scrollWidth > clientWidth;
      
    this.maxLength = items.length - (Math.floor(clientWidth / blockWidth) - 1);
    this.currentSlide = Math.round(scrollLeft / (blockWidth + gap)) + 1;

    items.forEach((item, index) => item.classList.toggle("active", index + 1 == this.currentSlide));
    
    if (this.prevButton && this.nextButton) {
      this.prevButton.toggleAttribute("disabled", this.currentSlide == 1);
      this.nextButton.toggleAttribute("disabled", this.currentSlide == this.maxLength);
  
      if (this.hideControls) {
        this.prevButton.classList.toggle("hidden", !hasScroll);
        this.nextButton.classList.toggle("hidden", !hasScroll);
      }
    }

    if (this.scrollbarThumb) {      
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const thumbPosition = (scrollLeft / (scrollWidth - clientWidth)) * 100;

      this.scrollbarThumb.style.left = `${thumbPosition}%`;
      this.scrollbarThumb.style.width = `${thumbWidth}%`;
      this.scrollbarThumb.style.transform = `translateX(-${thumbPosition}%)`;
      this.scrollbar.classList.toggle("hidden", !hasScroll);
    }

    if (this.pagination) {
      this.updatePagination();
    }

    if (this.counter) {
      this.counter.innerHTML = `${this.currentSlide} / ${items.length}`;
    }
  
    if (this.allowEvents) {
      const maxScroll = scrollWidth - clientWidth;

      this.dispatchEvent(
        new CustomEvent("slider-updated", {
          detail: {
            currentSlide: this.currentSlide,
            // 0..1 scroll progress, lets listeners follow the slider in real
            // time instead of reacting only when the active slide changes.
            progress: maxScroll > 0 ? scrollLeft / maxScroll : 0
          },
          bubbles: true,
        })
      );
    }
  }

  updatePagination() {
    const slides = this.querySelectorAll('[id^="Slide-"]');

    if (!this.slider || slides.length === 0 || !this.pagination) return;

    const containerWidth = this.slider.clientWidth;
    const slideWidth = slides[0].offsetWidth;

    const slidesPerPage = Math.floor(containerWidth / slideWidth);
    const totalPages = slides.length - (slidesPerPage - 1);

    this.pagination.innerHTML = '';
    this.pagination.classList.toggle("hidden", totalPages === 1);

    for (let i = 1; i <= totalPages; i++) {
      const dot = document.createElement('span');
      dot.setAttribute("data-slide", i);
      if (i === this.currentSlide) dot.classList.add("active");
      this.pagination.appendChild(dot);
    }
  }
}

customElements.define("slider-component", SliderComponent);

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.changeSibling = this.changeSibling.bind(this);
    this.addProduct = this.addProduct.bind(this);
    this.siblingsForm = this.querySelector('[id^="Siblings-"]');
    // a card can carry more than one add-to-cart: the round one on the photo
    // and the one in the price row.
    this.quickAdds = this.querySelectorAll('[id^="QuickAdd-"]');
    this.isLoading = false;
  }

  connectedCallback() {
    this.siblingsForm?.addEventListener('change', this.changeSibling);
    this.quickAdds.forEach(button => button.addEventListener('click', this.addProduct));
    this.revealHoverMedia();
    this.watchDeferredMedia();
  }

  // A card a carousel starts with off to one side ships its pictures as URLs.
  // They are built well before the card is reached, so scrolling the row never
  // waits on this; a card with no picture to build observes nothing.
  //
  // Without IntersectionObserver the picture is built straight away, which is
  // the old behaviour for that browser rather than a card with a hole in it.
  watchDeferredMedia() {
    const link = this.querySelector('.product-card__media[data-media]:not([data-sibling-media])');
    if (!link) return;

    if (!("IntersectionObserver" in window)) {
      this.hydrateSiblingMedia(link);
      return;
    }

    this.mediaObserver = new IntersectionObserver((entries, observer) => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      this.mediaObserver = null;
      this.hydrateSiblingMedia(link);
    }, { root: null, rootMargin: "600px" });

    this.mediaObserver.observe(this);
  }

  // The hover photo ships inside <template>, so touch devices never request it.
  // On a pointer device it is put back straight away, keeping the hover swap
  // instant; it is still loading="lazy", so cards below the fold wait their turn.
  revealHoverMedia() {
    if (!window.matchMedia("(hover: hover)").matches) return;

    this.querySelectorAll("template[data-card-hover]").forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  disconnectedCallback() {
    this.siblingsForm?.removeEventListener('change', this.changeSibling);
    this.quickAdds.forEach(button => button.removeEventListener('click', this.addProduct));
    this.mediaObserver?.disconnect();
  }

  // Only the colour on show arrives as markup. The others carry their two URLs
  // on the link and are built here, the first time the swatch is used — see
  // snippets/product-card-media. Nothing is fetched for the markup; the browser
  // only now downloads a picture it had no reason to want before.
  //
  // This is the second place a card picture is written, the Liquid snippet
  // being the first. Keep them in step: a change to how a card is pictured
  // belongs in both.
  hydrateSiblingMedia(link) {
    if (!link || !link.dataset.media || link.querySelector("img")) return;

    // Wrapped in <picture> because that is what the snippet renders — media
    // falls back to the desktop image for mobile, so its <picture> branch is
    // always the one taken. The link is a flex container, so a bare <img>
    // becomes the flex item itself and sizes a few pixels short of one sitting
    // inside a picture. Same shape, same height.
    const picture = (src, src2x, cls) => {
      const img = document.createElement("img");
      img.src = src;
      if (src2x) img.srcset = `${src} 1x, ${src2x} 2x`;
      img.className = cls;
      img.loading = "lazy";
      img.alt = link.dataset.mediaAlt || "";

      const wrapper = document.createElement("picture");
      wrapper.appendChild(img);
      return wrapper;
    };

    link.appendChild(picture(link.dataset.media, link.dataset.media2x, "image"));

    // The hover photo follows the same rule as the one the card ships with: it
    // can only ever be seen on a pointer device, so a touch screen never asks
    // for it.
    if (link.dataset.mediaHover && window.matchMedia("(hover: hover)").matches) {
      link.appendChild(
        picture(link.dataset.mediaHover, link.dataset.mediaHover2x, "image product-card__media--hover")
      );
    }
  }

  changeSibling(event) {
    event.preventDefault();
    const target = this.querySelector(`[data-sibling-media="${event.target.value}"]`);
    if (target) {
      this.hydrateSiblingMedia(target);
      this.querySelector("[data-sibling-media].active").classList.remove("active");
      target.classList.add("active");
    }

    const siblingCardData = JSON.parse(event.target.dataset.details);
  
    if (this.querySelector("[data-title]")) {
      this.querySelector("[data-title]").href = siblingCardData.product_url;
      this.querySelector("[data-title]").innerText = siblingCardData.title;
    }
  
    if (this.querySelector("[data-subtitle]")) {
      this.querySelector("[data-subtitle]").innerText = siblingCardData.subtitle;
    }
  
    if (this.querySelector("[data-price]")) {
      this.querySelector("[data-price]").innerHTML = siblingCardData.price;
    }

    if (this.querySelector("[data-compare]")) {
      this.querySelector("[data-compare]").innerHTML = siblingCardData.compare;
    }

    this.querySelectorAll("[data-variant]").forEach(el => {
      el.dataset.variant = siblingCardData.id;
    });

    // Each colour brings its own gift, or none, and its own stock: the button kept the
    // first colour's state, so a sold out colour could be tapped and failed silently.
    this.quickAdds.forEach(button => {
      button.disabled = siblingCardData.available === false;

      if (siblingCardData.gift) {
        button.dataset.giftVariant = siblingCardData.gift;
      } else {
        delete button.dataset.giftVariant;
      }
    });
  }

  addProduct(event) {
    if (event) event.preventDefault();

    if (this.isLoading) return;

    // currentTarget: the card has two quick adds, and a tap on an icon inside one
    // must still read the button's variant.
    const button = event.currentTarget;

    const sections = [];
    if (this.cart) sections.push("cart-drawer");

    const items = [
      {
        id: parseInt(button.dataset.variant),
        quantity: 1
      }
    ];

    // The product's own free gift, on the same terms as the product page: one per
    // order, bound to the product so removing it takes the gift along.
    const giftVariant = button.dataset.giftVariant;
    if (giftVariant && this.cart && !this.cart.hasAnyGift()) {
      const groupId = Date.now().toString();
      items[0].properties = { _gift_group: groupId };
      items.push({
        id: parseInt(giftVariant),
        quantity: 1,
        properties: { _gift_group: groupId, _gift: 'true' }
      });
    }

    // Same as the product page: the order gift goes in this request rather than
    // triggering another one once it lands.
    if (this.cart) items.push(...this.cart.addOnItems());

    const cartData = {
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    };

    this.toggleLoading();

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        const error = new Error(response.description);
        throw error;
      } else {
        this.toggleLoading();
        
        if (this.cart) {
          this.cart.renderContent(response.sections);
          this.cart.open();
        }
      }
    })
    .catch(error => this.showError(error))
    .finally(() => this.toggleLoading(true));
  }

  // The card had no showError, so a refused add ("only 2 left") threw inside the
  // catch and the customer got nothing at all.
  showError(error) {
    console.error(error);

    const messageBlock = this.querySelector("[card-notification]");
    if (!messageBlock) return;

    messageBlock.innerText = error.message;
    setTimeout(() => {messageBlock.innerText = ""}, 5000);
  }

  toggleLoading(turnOff = false) {
    this.isLoading = !this.isLoading;
    if (turnOff) this.isLoading = false;
    this.classList.toggle("loading", this.isLoading);
  }
}

customElements.define("product-card", ProductCard);

// Tiers are in shop currency cents and have to match the bundle-discount function config
// (safecourt-x-ecomgrows, discount metafield $app.function-configuration, in whole units).
const BUNDLE_TIERS = [
  { min: 15000, percent: 5 },
  { min: 22500, percent: 10 },
  { min: 30000, percent: 15 },
  { min: 50000, percent: 20 }
];
const BUNDLE_MIN_ITEMS = 2;
const BUNDLE_STORAGE_KEY = "safecourt:bundle-builder";
const BUNDLE_GIFT_STORAGE_KEY = "safecourt:bundle-builder-gift";

class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.bar = this.querySelector("[data-bar]");
    this.checkoutButton = this.querySelector('[data-action="checkout"]');
    this.lineTemplate = this.querySelector("[data-line-template]");
    this.moneyFormat = this.dataset.moneyFormat;
    this.cards = Array.from(this.querySelectorAll("[data-card]"));
    this.isLoading = false;

    this.variants = new Map();
    this.cards.forEach((card) => {
      JSON.parse(card.querySelector("[data-variants]").textContent).forEach((variant) => {
        this.variants.set(String(variant.id), variant);
      });
    });

    // Same conversion as the free shipping threshold in the cart drawer.
    const rate = parseFloat(window.Shopify?.currency?.rate || 1);
    this.tiers = BUNDLE_TIERS.map((tier) => ({
      percent: tier.percent,
      min: Math.round((tier.min * rate) / 100) * 100
    }));

    // Free gift: threshold in whole shop-currency units, converted and rounded the same
    // way the bundle discount function does it, so both agree on when it unlocks.
    this.giftOptions = Array.from(this.querySelectorAll("[data-gift-variant]"));
    this.giftMin = this.dataset.giftMin ? Math.round(parseFloat(this.dataset.giftMin) * rate) * 100 : null;
    this.gift = this.restoreGift();
    this.unlockedGift = null;

    this.items = this.restore();
  }

  connectedCallback() {
    this.addEventListener("click", this.onClick);
    window.addEventListener("pageshow", this.onPageShow);
    this.renderTicks();
    this.render();
    this.watchCategories();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    window.removeEventListener("pageshow", this.onPageShow);
    this.categoryObserver?.disconnect();
  }

  // Highlights the category in the sticky row that is currently on screen, and keeps
  // that chip scrolled into view on mobile where the row overflows.
  watchCategories() {
    const links = Array.from(this.querySelectorAll("[data-category-nav] [data-action='scroll-to']"));
    if (!links.length || !("IntersectionObserver" in window)) return;

    const setActive = (id) => {
      links.forEach((link) => {
        const active = link.getAttribute("href") === `#${id}`;
        link.classList.toggle("active", active);
        if (active) {
          const row = link.parentElement;
          row.scrollTo({ left: link.offsetLeft - row.offsetLeft - 16, behavior: "smooth" });
        }
      });
    };

    // A category counts as current once it crosses a line just under the sticky row.
    this.categoryObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );

    links.forEach((link) => {
      const category = document.getElementById(link.getAttribute("href").slice(1));
      if (category) this.categoryObserver.observe(category);
    });
  }

  // Back from the checkout the page comes out of the bfcache with the spinner still on.
  onPageShow(event) {
    if (event.persisted) this.toggleLoading(false);
  }

  onClick(event) {
    const target = event.target;
    const card = target.closest("[data-card]");

    switch (target.dataset.action) {
      case "scroll-to":
        event.preventDefault();
        document.getElementById(target.getAttribute("href").slice(1))?.scrollIntoView({ behavior: "smooth" });
        break;
      case "select-option":
      case "select-model":
        this.selectOption(card, target.dataset.variant);
        break;
      case "add":
      case "increase":
        this.changeQuantity(target.dataset.variant || card?.dataset.variant, 1);
        break;
      case "decrease":
        this.changeQuantity(target.dataset.variant || card?.dataset.variant, -1);
        break;
      case "toggle-bar":
        this.toggleBar();
        break;
      case "checkout":
        this.checkout();
        break;
      case "select-gift":
        this.gift = target.dataset.giftVariant;
        this.saveGift();
        this.render();
        break;
    }
  }

  restore() {
    const items = new Map();

    try {
      const saved = JSON.parse(localStorage.getItem(BUNDLE_STORAGE_KEY)) || [];
      saved.forEach(([id, quantity]) => {
        // Products removed from the page or sold out since the last visit are dropped.
        if (this.variants.get(String(id))?.available && quantity > 0) {
          items.set(String(id), parseInt(quantity, 10));
        }
      });
    } catch (error) {}

    return items;
  }

  save() {
    try {
      localStorage.setItem(BUNDLE_STORAGE_KEY, JSON.stringify(Array.from(this.items)));
    } catch (error) {}
  }

  restoreGift() {
    try {
      const saved = localStorage.getItem(BUNDLE_GIFT_STORAGE_KEY);
      return this.giftOptions.some((option) => option.dataset.giftVariant === saved) ? saved : null;
    } catch (error) {
      return null;
    }
  }

  saveGift() {
    try {
      localStorage.setItem(BUNDLE_GIFT_STORAGE_KEY, this.gift || "");
    } catch (error) {}
  }

  selectOption(card, id) {
    const variant = this.variants.get(id);
    if (!card || !variant) return;

    card.dataset.variant = id;
    card.querySelectorAll("[data-media]").forEach((media) => {
      media.classList.toggle("active", media.dataset.media === id);
    });
    card.querySelectorAll('[data-action="select-option"]').forEach((option) => {
      option.setAttribute("aria-pressed", option.dataset.variant === id);
    });

    // "Model + color" cards: only the colors of the picked model are shown.
    const model = card.querySelector(`[data-action="select-option"][data-variant="${id}"]`)?.dataset.model;
    if (model) {
      card.querySelectorAll('[data-action="select-model"]').forEach((option) => {
        option.setAttribute("aria-pressed", option.dataset.model === model);
      });
      card.querySelectorAll('[data-action="select-option"][data-model]').forEach((option) => {
        option.classList.toggle("hidden", option.dataset.model !== model);
      });
    }

    card.querySelectorAll("[data-hide-for]").forEach((benefit) => {
      benefit.classList.toggle("hidden", benefit.dataset.hideFor.includes(`,${id},`));
    });
    card.querySelector("[data-title]").textContent = variant.title;
    card.querySelector("[data-tagline]").textContent = variant.tagline;
    card.querySelector("[data-price]").textContent = variant.price_formatted;
    card.querySelector("[data-compare]").textContent = variant.compare_formatted;

    this.renderCard(card);
  }

  changeQuantity(id, change) {
    const variant = this.variants.get(id);
    if (!variant || this.isLoading) return;

    const quantity = (this.items.get(id) || 0) + change;
    if (change > 0 && !variant.available) return;

    if (quantity > 0) {
      this.items.set(id, quantity);
    } else {
      this.items.delete(id);
    }

    this.save();
    this.render();
  }

  toggleBar() {
    const open = this.bar.classList.toggle("open");
    this.bar.querySelector(".bundle-builder__bar-head").setAttribute("aria-expanded", open);
  }

  getTotals() {
    let count = 0;
    let subtotal = 0;

    this.items.forEach((quantity, id) => {
      count += quantity;
      subtotal += this.variants.get(id).price * quantity;
    });

    const reached = this.tiers.filter((tier) => subtotal >= tier.min).pop();
    const percent = count >= BUNDLE_MIN_ITEMS && reached ? reached.percent : 0;

    return { count, subtotal, percent };
  }

  formatWhole(cents) {
    const format = (this.moneyFormat || "").replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) =>
      placeholder.includes("comma") ? "{{amount_no_decimals_with_comma_separator}}" : "{{amount_no_decimals}}"
    );
    return formatMoney(cents, format);
  }

  renderTicks() {
    const progress = this.querySelector("[data-progress]");
    const scale = this.tiers[this.tiers.length - 1].min * 1.12;

    this.tiers.forEach((tier) => {
      const tick = document.createElement("span");
      tick.className = "bundle-builder__tick";
      tick.style.left = `${(tier.min / scale) * 100}%`;
      tick.dataset.min = tier.min;
      tick.innerHTML = "<span></span><span></span>";
      tick.children[0].textContent = this.formatWhole(tier.min);
      tick.children[1].textContent = `${tier.percent}%`;
      progress.append(tick);
    });

    this.progressScale = scale;

    // Mobile bar: one evenly spaced step per tier, the last one at the end of the track.
    const meter = this.querySelector("[data-nudge-meter]");
    this.tiers.forEach((tier, index) => {
      const step = document.createElement("span");
      step.className = "bundle-builder__nudge-point";
      step.style.left = `${((index + 1) / this.tiers.length) * 100}%`;
      step.dataset.min = tier.min;
      step.textContent = `${tier.percent}%`;
      meter.append(step);
    });
  }

  // Fill of the stepped mobile track: each tier owns an equal stretch, filled by how far
  // the subtotal is between the previous tier and this one.
  nudgeFill(subtotal) {
    const count = this.tiers.length;
    for (let index = 0; index < count; index++) {
      const from = index === 0 ? 0 : this.tiers[index - 1].min;
      const to = this.tiers[index].min;
      if (subtotal < to) return ((index + (subtotal - from) / (to - from)) / count) * 100;
    }
    return 100;
  }

  renderCard(card) {
    const quantity = this.items.get(card.dataset.variant) || 0;

    card.classList.toggle("active", quantity > 0);
    card.querySelector('[data-action="add"]').classList.toggle("hidden", quantity > 0);
    card.querySelector("[data-quantity]").classList.toggle("hidden", quantity === 0);
    card.querySelector("[data-quantity-label]").textContent = this.dataset.labelInBundle.replace("__quantity__", quantity);
  }

  render() {
    const { count, subtotal, percent } = this.getTotals();
    const discount = Math.round((subtotal * percent) / 100);

    this.cards.forEach((card) => this.renderCard(card));

    const lines = this.querySelector("[data-lines]");
    lines.replaceChildren(
      ...Array.from(this.items).map(([id, quantity]) => {
        const variant = this.variants.get(id);
        const line = this.lineTemplate.content.firstElementChild.cloneNode(true);
        const price = variant.price * quantity;
        const lineDiscount = Math.round((price * percent) / 100);
        const image = line.querySelector("[data-line-image]");

        if (variant.image) {
          image.src = variant.image;
        } else {
          image.removeAttribute("loading");
        }
        line.querySelector("[data-line-title]").textContent = variant.color ? `${variant.title} - ${variant.color}` : variant.title;
        line.querySelector("[data-line-quantity]").textContent = quantity;
        line.querySelector("[data-line-price]").textContent = formatMoney(price - lineDiscount, this.moneyFormat);
        line.querySelector("[data-line-compare]").textContent = lineDiscount > 0 ? formatMoney(price, this.moneyFormat) : "";
        line.querySelectorAll("[data-action]").forEach((button) => (button.dataset.variant = id));
        return line;
      })
    );

    this.querySelector("[data-empty]").classList.toggle("hidden", count > 0);
    this.querySelector("[data-subtotal]").textContent = formatMoney(subtotal, this.moneyFormat);
    this.querySelector("[data-discount]").textContent = formatMoney(discount, this.moneyFormat);
    this.querySelector("[data-total]").textContent = formatMoney(subtotal - discount, this.moneyFormat);

    const next = this.tiers.find((tier) => subtotal < tier.min);
    this.querySelector("[data-progress-label]").textContent = this.tierLabel(count, subtotal, next);

    this.querySelector("[data-progress-fill]").style.width = `${Math.min((subtotal / this.progressScale) * 100, 100)}%`;

    // Collapsed mobile bar: same tier line over a stepped track (5% · 10% · …).
    this.querySelector("[data-nudge]").textContent = this.querySelector("[data-progress-label]").textContent;
    this.querySelector("[data-nudge-fill]").style.width = `${this.nudgeFill(subtotal)}%`;
    this.querySelectorAll(".bundle-builder__nudge-point").forEach((point) => {
      point.classList.toggle("reached", subtotal >= parseInt(point.dataset.min, 10));
    });
    this.querySelectorAll(".bundle-builder__tick").forEach((tick) => {
      tick.classList.toggle("reached", subtotal >= parseInt(tick.dataset.min, 10));
    });

    const remaining = BUNDLE_MIN_ITEMS - count;
    const notice = this.querySelector("[data-notice]");
    notice.classList.toggle("hidden", count === 0 || remaining <= 0);
    notice.textContent = (remaining === 1 ? this.dataset.labelMinItemsOne : this.dataset.labelMinItemsOther).replace(
      "__remaining__",
      remaining
    );

    this.querySelector("[data-count]").textContent = count;
    this.querySelector("[data-summary-total]").textContent = formatMoney(subtotal - discount, this.moneyFormat);
    this.querySelector("[data-summary-discount]").textContent = percent
      ? `· ${this.dataset.labelDiscountShort.replace("__percent__", percent)}`
      : "";

    this.renderGift(count, subtotal);

    this.checkoutButton.disabled = count < BUNDLE_MIN_ITEMS || this.isLoading;
    this.showError("");
  }

  // Free gift block. Locked: how much is still missing. Unlocked: the options become
  // clickable, the first one is picked straight away so nobody reaches the checkout
  // without it, and the gift shows up in the totals.
  renderGift(count, subtotal) {
    const block = this.querySelector("[data-gift]");
    if (!block || this.giftMin === null || !this.giftOptions.length) return;

    const unlocked = count >= BUNDLE_MIN_ITEMS && subtotal >= this.giftMin;
    if (unlocked && !this.giftOptions.some((option) => option.dataset.giftVariant === this.gift)) {
      this.gift = this.giftOptions[0].dataset.giftVariant;
      this.saveGift();
    }
    this.unlockedGift = unlocked ? this.gift : null;

    let label;
    if (unlocked) {
      label = this.dataset.labelGiftChoose;
    } else if (count === 0 || subtotal >= this.giftMin) {
      label = this.dataset.labelGiftFrom.replace("__amount__", this.wholeUp(this.giftMin));
    } else {
      label = this.dataset.labelGiftLocked.replace("__amount__", this.wholeUp(this.giftMin - subtotal));
    }

    block.classList.toggle("unlocked", unlocked);
    block.querySelector("[data-gift-label]").textContent = label;
    this.giftOptions.forEach((option) => {
      option.disabled = !unlocked;
      option.setAttribute("aria-pressed", unlocked && option.dataset.giftVariant === this.gift);
    });

    const chosen = unlocked ? this.giftOptions.find((option) => option.dataset.giftVariant === this.gift) : null;
    const row = this.querySelector("[data-gift-row]");
    row.classList.toggle("hidden", !chosen);
    if (chosen) {
      row.querySelector("[data-gift-row-label]").textContent = this.dataset.labelGiftRow
        .replace("__title__", chosen.dataset.giftTitle)
        .replace("__price__", formatMoney(parseInt(chosen.dataset.giftPrice, 10), this.moneyFormat));
    }

    const hint = this.querySelector("[data-gift-hint]");
    if (hint) hint.textContent = unlocked ? this.dataset.labelGiftUnlocked : label;
  }

  // Whole units, rounded up, so adding exactly that much always gets there.
  wholeUp(cents) {
    return this.formatWhole(Math.ceil(cents / 100) * 100);
  }

  // Tier line under the bundle, kept to one short sentence:
  //   empty                     "5% korting vanaf €150"
  //   tier met, one item short  "Nog 1 product voor 10% korting"
  //   below the first tier      "Nog €35 tot 5% korting"
  //   a tier active             "10% korting actief. Nog €75 tot 15%"
  //   top tier                  "Je hebt de maximale korting: 20%"
  // Amounts are rounded up to whole units, so adding exactly that much always gets there.
  tierLabel(count, subtotal, next) {
    const reached = this.tiers.filter((tier) => subtotal >= tier.min).pop();
    const whole = (cents) => this.wholeUp(cents);

    if (count === 0) {
      return this.dataset.labelEmptyTier
        .replace("__amount__", whole(this.tiers[0].min))
        .replace("__percent__", this.tiers[0].percent);
    }

    if (count < BUNDLE_MIN_ITEMS && reached) {
      return this.dataset.labelOneProductTier.replace("__percent__", reached.percent);
    }

    if (!next) return this.dataset.labelMaxTier.replace("__percent__", reached.percent);

    const label = reached ? this.dataset.labelActiveTier.replace("__active__", reached.percent) : this.dataset.labelNextTier;
    return label.replace("__amount__", whole(next.min - subtotal)).replace("__percent__", next.percent);
  }

  showError(message) {
    const error = this.querySelector("[data-error]");
    error.textContent = message;
    error.classList.toggle("hidden", !message);
  }

  toggleLoading(isLoading) {
    this.isLoading = isLoading;
    this.checkoutButton.classList.toggle("loading", isLoading);
    this.checkoutButton.disabled = isLoading || this.getTotals().count < BUNDLE_MIN_ITEMS;
  }

  // Straight to the checkout, past the cart drawer. The cart is emptied first, so the
  // checkout holds only this bundle. Every product stays its own line; the discount
  // function groups lines by _bundle_id. An unlocked gift goes in as one more line of
  // the bundle, marked _bundle_gift, which the same function makes free.
  checkout() {
    if (this.isLoading || this.getTotals().count < BUNDLE_MIN_ITEMS) return;

    this.showError("");
    this.toggleLoading(true);

    const bundleId = "b-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);

    fetch(window.Shopify.routes.root + "cart/clear.js", { method: "POST" })
      .then((response) => {
        if (!response.ok) throw new Error();

        return fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              ...Array.from(this.items).map(([id, quantity]) => ({
                id: parseInt(id, 10),
                quantity: quantity,
                properties: { _bundle_id: bundleId }
              })),
              ...(this.unlockedGift
                ? [{ id: parseInt(this.unlockedGift, 10), quantity: 1, properties: { _bundle_id: bundleId, _bundle_gift: "true", _bundle_gift_min: this.dataset.giftMin } }]
                : [])
            ]
          })
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (!response.items) throw Object.assign(new Error(), { description: response.description });
        // A redirect to /checkout from JS lost the market and the language, and in the
        // Instagram and Facebook browsers it did not always fire at all.
        this.querySelector("[data-checkout-form]").submit();
      })
      .catch((error) => {
        this.toggleLoading(false);
        // Shopify's own message is translated ("only 3 left"); a network error is not.
        this.showError(error.description || this.dataset.labelError);
      });
  }
}

if (document.querySelector("bundle-builder")) customElements.define("bundle-builder", BundleBuilder);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.giftBusy = false;
    this.insuranceBusy = false;
    this.onClickEvent = this.onClickEvent.bind(this);
    this.onSubmitEvent = this.onSubmitEvent.bind(this);
    this.onChangeEvent = this.onChangeEvent.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onUpsellScroll = this.onUpsellScroll.bind(this);

    // Variants added from an upsell card while the drawer is open. Their cards stay
    // on screen, ticked, though the cart now holds them; closing the drawer lets go.
    this.upsellAdded = new Set();
  }

  connectedCallback() {
    this.addEventListener("click", this.onClickEvent)
    this.addEventListener("submit", this.onSubmitEvent);
    this.addEventListener("change", this.onChangeEvent);
    // Scroll does not bubble, so the carousels are watched in the capture phase.
    this.addEventListener("scroll", this.onUpsellScroll, true);
    window.addEventListener("pageshow", this.onPageShow);

    document.addEventListener("click", (e) => {
      if (e.target.hasAttribute("cart-drawer-toggle")) {
        this.open();
      }
    })

    this.updateFreeShipping();
    this.updateTimer();
    this.reconcileShippingInsurance();
    this.applyUpsellState();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClickEvent)
    this.removeEventListener("submit", this.onSubmitEvent);
    this.removeEventListener("change", this.onChangeEvent);
    this.removeEventListener("scroll", this.onUpsellScroll, true);
    window.removeEventListener("pageshow", this.onPageShow);
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.cartBusyWatchdog) clearTimeout(this.cartBusyWatchdog);
  }

  // Every cart change — an item quantity, the gift, the insurance switch — runs
  // through here, so the switch, its spinner and the checkout button can never
  // disagree about whether the cart is settled.
  setCartBusy() {
    this.applyCartBusy(this.isLoading || this.giftBusy || this.insuranceBusy);
  }

  // Split from setCartBusy so the watchdog can release the controls without
  // touching flags that belong to a request still in flight.
  applyCartBusy(busy) {
    clearTimeout(this.cartBusyWatchdog);

    this.classList.toggle("cart-busy", busy);

    // Both of these are re-rendered from the section on every cart change, so the
    // state is re-applied here rather than assumed to have survived.
    //
    // Locked for any cart change, because a second request must not race the one in
    // flight — but only spinning for its own, so adding an upsell does not set the
    // insurance switch spinning over a line that is not moving.
    const toggle = this.querySelector(".cart-drawer__insurance-toggle");
    if (toggle) {
      toggle.disabled = busy;
      toggle.setAttribute("aria-busy", busy && this.insuranceBusy ? "true" : "false");
    }

    const checkout = this.querySelector('[data-checkout-form] [name="checkout"]');
    if (checkout) checkout.setAttribute("aria-disabled", busy ? "true" : "false");

    // A request that never settles must not leave the checkout button dead for the
    // rest of the session — that is a lost order, not a cosmetic bug. This only
    // hands the controls back; the flags stay as their owners left them, and the
    // next cart change re-renders the truth.
    if (busy) {
      this.cartBusyWatchdog = setTimeout(() => this.applyCartBusy(false), 10000);
    }
  }

  // By line key, not by position: a cart changed in another tab, or a gift the
  // reconcile has just put in, shifts the positions and the change would land on
  // a different line.
  updateQuantity(key, quantity, itemEl = false) {
    this.toggleLoading(itemEl);

    const data = {
      id: key,
      quantity: quantity,
      sections: ["cart-drawer"],
      sections_url: window.location.pathname
    }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(response => {
      if (response.sections) {
        this.renderContent(response.sections);
      } else {
        throw new Error(response.description);
      }
    })
    .catch((error) => {
      console.error('Error:', error);

      // The line may be gone from the list by now; the quantity shown is put back
      // to what the cart holds, since the input may carry a number typed by hand.
      const lineEl = this.querySelector(`[data-key="${key}"]`);
      const messageBlock = lineEl?.querySelector("[item-notification]");
      const input = lineEl?.querySelector('[name="quantity"]');
      if (input) input.value = input.defaultValue;
      if (!messageBlock) return;

      messageBlock.innerText = error.message;
      setTimeout(() => {messageBlock.innerText = ""}, 5000);
    })
    .finally(() => {this.toggleLoading(itemEl)});
  }

  removeItem(itemEl) {
    const group = itemEl.dataset.giftGroup;
    const isGift = itemEl.dataset.gift === 'true';

    // Removing the main product of a gift group → remove the gift together with it.
    // Removing the gift itself leaves the main product untouched.
    if (group && !isGift) {
      const giftEl = this.querySelector(`[data-gift-group="${group}"][data-gift="true"]`);

      if (giftEl && itemEl.dataset.key && giftEl.dataset.key) {
        const updates = {};
        updates[itemEl.dataset.key] = 0;
        updates[giftEl.dataset.key] = 0;
        this.updateCart(updates, [itemEl, giftEl]);
        return;
      }
    }

    this.updateQuantity(itemEl.dataset.key, 0, itemEl);
  }

  updateCart(updates, itemEls = []) {
    this.toggleLoading();
    itemEls.forEach((el) => el && el.classList.add("loading"));

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        updates: updates,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then(response => response.json())
    .then(response => {
      if (response.sections) {
        this.renderContent(response.sections);
      } else {
        throw new Error(response.description);
      }
    })
    .catch(error => console.error('Error:', error))
    .finally(() => {
      this.toggleLoading();
      itemEls.forEach((el) => el && el.classList.remove("loading"));
    });
  }

  renderContent(sections) {
    if (!sections['cart-drawer']) return;

    // The markup below is the cart as the server now sees it, add-ons included,
    // so the next caller can read it instead of being held off.
    this.addOnsPending = false;

    const html = new DOMParser().parseFromString(sections['cart-drawer'], "text/html");
    const newCartDrawer = html.querySelector("cart-drawer");

    // The carousels are rebuilt with the rest of the list, which would throw the
    // customer back to the first card after every add.
    const upsellScroll = [...this.querySelectorAll("[data-upsell-list]")].map((list) => list.scrollLeft);

    this.querySelector("[js-content-items]").innerHTML = newCartDrawer.querySelector("[js-content-items]").innerHTML;

    // Ticks first, scroll second: a card that only reappears after the scroll is put
    // back makes the row re-snap one card further along, away from that very card.
    this.applyUpsellState();
    this.querySelectorAll("[data-upsell-list]").forEach((list, index) => {
      if (!upsellScroll[index]) return;
      list.scrollLeft = upsellScroll[index];
      const block = list.closest("[data-upsell]");
      if (block) this.updateUpsellArrows(block);
    });
    this.querySelector("[js-content-footer]").innerHTML = newCartDrawer.querySelector("[js-content-footer]").innerHTML;
    this.setAttribute("data-count", newCartDrawer.dataset.count);
    this.setAttribute("data-cart-total", newCartDrawer.dataset.cartTotal);
    this.setAttribute("data-free-shipping-total", newCartDrawer.dataset.freeShippingTotal);

    // The add-on sits outside both re-rendered regions, so its switch and its line
    // key are swapped in by hand rather than coming along with the item list.
    const insurance = this.querySelector("[js-shipping-insurance]");
    const newInsurance = newCartDrawer.querySelector("[js-shipping-insurance]");

    // Replacing the element throws away the focus on it, which would drop a keyboard
    // or screen-reader user out of the drawer every time they used the switch.
    let insuranceHadFocus = false;

    if (insurance && newInsurance) {
      insuranceHadFocus = insurance.contains(document.activeElement);
      insurance.replaceWith(newInsurance);
    }

    this.updateFreeShipping();
    this.updateTimer();

    document.querySelectorAll("[cart-item-count]").forEach(countEl => countEl.innerText = newCartDrawer.dataset.count);

    // One cart change at a time. The gift goes first, and the re-render its own
    // request triggers brings us back here for the insurance — two carts/add.js in
    // flight together would each render a section that does not know about the other.
    if (!this.reconcileGift() && !this.reconcileBundleGift()) this.reconcileShippingInsurance();

    // Last, so it sees whether a reconcile above has just taken the cart over again.
    // The controls above were replaced with fresh markup a few lines up, and this is
    // what puts them back into the state the cart is actually in — one synchronous
    // task, so the enabled markup is never painted in between.
    this.setCartBusy();

    // After setCartBusy, or disabling the switch would take the focus straight back
    // off it. preventScroll so the drawer does not jump to the add-on.
    if (insuranceHadFocus) {
      const toggle = this.querySelector(".cart-drawer__insurance-toggle");
      if (toggle && !toggle.disabled) toggle.focus({ preventScroll: true });
    }
  }

  // What the order gift would need, for a request that is about to put a product
  // in the cart. Shipping insurance never rides along: it starts switched off and
  // only the customer puts it in, with the switch in the drawer.
  //
  // The gift used to be added by the reconcile method below, firing after the
  // drawer had already re-rendered the add: one click cost extra requests and
  // renders, and the button stayed locked through all of it. The decision does
  // not actually need the round trip. The drawer carries the cart
  // the server last rendered and is re-rendered on every change, so the same
  // rules can be read off it before the first request and the lines sent along
  // with the product.
  //
  // The reconciles stay exactly as they are. They are the net that catches a
  // cart changed in another tab, and they still own every other route in: a
  // quantity change, a removal, a card quick add from a page with no drawer.
  // After a request built here they find their work already done and do nothing.
  addOnItems() {
    const items = [];

    // Two cards tapped before the first response lands would each read a cart
    // with no gift in it and each bring one — the bug giftBusy used to catch
    // when the gift had its own request. The flag lives on the drawer because
    // the cards know nothing about each other, and renderContent clears it:
    // that is the moment the cart the next caller reads is true again. A failed
    // request leaves it set, and the reconcile on the next render puts the gift
    // in instead, which is the behaviour this replaced anyway.
    if (this.addOnsPending) return items;

    // One order gift per order, from the moment the cart holds a product. A
    // gift tied to a product carries a group and does not stand in for it —
    // same rule as reconcileGift, deliberately asymmetric with the product
    // gift, which is the one that is limited to one of any kind.
    const giftVariant = this.dataset.orderGift;
    if (giftVariant && !this.querySelector('[data-gift="true"]:not([data-gift-group])')) {
      items.push({ id: parseInt(giftVariant), quantity: 1, properties: { _gift: 'true' } });
    }

    if (items.length) this.addOnsPending = true;

    return items;
  }

  // True when the cart already holds a gift of any kind. The product gift is
  // limited to one per order across both kinds, so this is what decides whether
  // a product brings its own.
  hasAnyGift() {
    return !!this.querySelector('[data-gift="true"]');
  }

  // The order gift belongs to the cart, not to whatever page it was added from.
  // Reconciling here means every route into the cart is covered by one rule:
  // add-to-cart on a product page, a card on the homepage or a collection, a
  // quantity change, a removal, an upsell inside the drawer.
  // Returns true when it has taken the cart over: a change is in flight, and nothing
  // else should touch the cart until the re-render that follows.
  reconcileGift() {
    if (this.giftBusy) return true;

    const giftVariant = this.dataset.orderGift;
    if (!giftVariant) return false;

    // A gift attached to a product carries its group and is that product's
    // business; the order gift has none and is ours to manage.
    const giftLine = this.querySelector('[data-gift="true"]:not([data-gift-group])');
    const productCount = this.querySelectorAll('[data-item]:not([data-gift])').length;

    if (productCount > 0 && !giftLine) {
      this.changeGift({
        url: 'cart/add.js',
        body: {
          items: [{ id: parseInt(giftVariant), quantity: 1, properties: { _gift: 'true' } }]
        }
      });
      return true;
    } else if (productCount === 0 && giftLine) {
      // By line key rather than variant id: the same variant can sit in the cart
      // more than once with different properties.
      this.changeGift({
        url: 'cart/change.js',
        body: { id: giftLine.dataset.key, quantity: 0 }
      });
      return true;
    }

    return false;
  }

  // The bundle builder's gift is earned by its bundle, so it goes once the bundle no
  // longer earns it: its products were taken out, or the bundle dropped under the
  // gift minimum. Left in, the store's own free gift discounts kept it at zero on
  // its own. Same terms as the builder and the bundle discount: at least
  // BUNDLE_MIN_ITEMS units, and the minimum (whole shop currency, carried on the
  // gift line) converted and rounded to a whole unit. A gift line from before the
  // minimum was carried goes only with its products.
  reconcileBundleGift() {
    if (this.giftBusy) return true;

    const giftLine = Array.from(this.querySelectorAll("[data-bundle-gift]")).find((line) => {
      const lines = Array.from(this.querySelectorAll(`[data-bundle-id="${line.dataset.bundleId}"]:not([data-bundle-gift])`));
      const units = lines.reduce((sum, el) => sum + (parseInt(el.querySelector('[name="quantity"]')?.value, 10) || 0), 0);
      const subtotal = lines.reduce((sum, el) => sum + (parseInt(el.dataset.linePrice, 10) || 0), 0);
      const min = parseFloat(line.dataset.bundleGift);
      const rate = Number(window.Shopify?.currency?.rate) || 1;

      if (!lines.length) return true;
      if (Number.isNaN(min)) return false;
      return units < BUNDLE_MIN_ITEMS || subtotal < Math.round(min * rate) * 100;
    });

    if (!giftLine) return false;

    this.changeGift({
      url: 'cart/change.js',
      body: { id: giftLine.dataset.key, quantity: 0 }
    });
    return true;
  }

  changeGift({ url, body }) {
    // Guards the re-render this triggers from reconciling again mid-flight, and
    // two quick adds from both deciding the gift is missing.
    this.giftBusy = true;
    this.setCartBusy();

    fetch(window.Shopify.routes.root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then(response => response.json())
    .then(response => {
      this.giftBusy = false;
      // renderContent ends in setCartBusy; this covers the response that carries no
      // sections, where there is nothing to re-render and the controls would stay
      // locked.
      if (response.sections) this.renderContent(response.sections);
      else this.setCartBusy();
    })
    // A failed request leaves the cart as it is rather than guessing; the next
    // thing the customer does will reconcile it.
    .catch(error => {
      this.giftBusy = false;
      this.setCartBusy();
      console.error('Error:', error);
    });
  }

  // The insurance add-on is offered to everyone and starts switched off: it goes in
  // only when the customer moves the switch (toggleShippingInsurance). From then on
  // it follows the cart the way the order gift does: out again when the last product
  // goes, and never in the cart more than once.
  reconcileShippingInsurance() {
    // Deliberately not guarded on isLoading: renderContent reaches here from inside
    // the add's own .then, while that flag is still set. giftBusy and insuranceBusy
    // are the ones that would mean a second cart request over one in flight.
    const addon = this.querySelector("[js-shipping-insurance]");
    if (!addon || this.giftBusy || this.insuranceBusy) return;

    const key = addon.dataset.key || "";
    const quantity = parseInt(addon.dataset.quantity, 10) || 0;
    const productCount = this.querySelectorAll('[data-item]:not([data-gift])').length;

    // The customer took their products out, and insurance on nothing is not a cart
    // they should be able to reach the checkout with.
    if (productCount === 0) {
      if (key) this.changeInsurance({ url: 'cart/change.js', body: { id: key, quantity: 0 } });
      return;
    }

    // One per order, whichever way a second one got in.
    if (key && quantity > 1) {
      this.changeInsurance({ url: 'cart/change.js', body: { id: key, quantity: 1 } });
    }
  }

  toggleShippingInsurance(button) {
    // Three guards on one action, because a double tap here means two cart requests
    // racing: the button is disabled, the click case checks, and so does this.
    if (this.isLoading || this.giftBusy || this.insuranceBusy) return;

    const addon = button.closest("[js-shipping-insurance]");
    if (!addon) return;

    const key = addon.dataset.key || "";

    // Move the switch now rather than after the round trip. The re-render is what
    // the state actually comes from, so a failed request corrects it back.
    button.setAttribute("aria-checked", key ? "false" : "true");

    if (key) {
      this.changeInsurance({ url: 'cart/change.js', body: { id: key, quantity: 0 } });
    } else {
      this.changeInsurance({
        url: 'cart/add.js',
        body: {
          items: [{
            id: parseInt(addon.dataset.variant),
            quantity: 1,
            properties: { _shipping_insurance: 'true' }
          }]
        }
      });
    }
  }

  changeInsurance({ url, body }) {
    // Guards the re-render this triggers from reconciling again mid-flight, and two
    // quick taps on the switch from both deciding the add-on is missing. The switch
    // is disabled and its spinner shown from the same flag, so the guard and what
    // the customer sees are one decision.
    this.insuranceBusy = true;
    this.setCartBusy();

    fetch(window.Shopify.routes.root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: ["cart-drawer"],
        sections_url: window.location.pathname
      })
    })
    .then(response => response.json())
    .then(response => {
      this.insuranceBusy = false;
      if (response.sections) this.renderContent(response.sections);
      else this.setCartBusy();
    })
    // A failed request leaves the cart as it is, so the switch has to be put back:
    // it was moved ahead of the response, and nothing re-renders to correct it.
    // Showing insurance as on when the cart does not hold it is the one state this
    // must never be left in.
    .catch(error => {
      this.insuranceBusy = false;
      this.syncInsuranceSwitch();
      this.setCartBusy();
      console.error('Error:', error);
    });
  }

  // The switch reads its truth from the line key the section rendered, never from
  // what was last clicked.
  syncInsuranceSwitch() {
    const addon = this.querySelector("[js-shipping-insurance]");
    if (!addon) return;

    const toggle = addon.querySelector(".cart-drawer__insurance-toggle");
    if (toggle) toggle.setAttribute("aria-checked", addon.dataset.key ? "true" : "false");
  }

  addItemToCart(item, itemEl = false) {
    this.toggleLoading(itemEl);

    const sections = ["cart-drawer"];

    // An upsell added from inside the drawer is the first product in an empty
    // cart often enough to matter, and then the gift was another request on top
    // of it.
    const items = [{ id: parseInt(item), quantity: 1 }, ...this.addOnItems()];

    const cartData = {
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(cartData),
    })
    .then(response => response.json())
    .then(response => {
      if (!response.items) {
        throw new Error(response.description);
      } else {
        // Before the render, so the fresh card comes in ticked instead of hidden.
        if (itemEl && itemEl.classList?.contains("cart-drawer__upsell-item")) this.upsellAdded.add(String(item));
        this.renderContent(response.sections);
      }
    })
    .catch(error => console.error(error))
    .finally(() => this.toggleLoading(itemEl));
  }

  // Cards for products already in the cart come hidden from the server. The ones
  // added from this drawer are shown again, ticked; a block with nothing left to
  // show is hidden, and the arrows follow what the row can still scroll to.
  applyUpsellState() {
    this.querySelectorAll(".cart-drawer__upsell-item").forEach((card) => {
      const variant = card.dataset.variant;
      if (!card.classList.contains("is-in-cart")) {
        // Taken out of the cart again: the card is an offer once more.
        this.upsellAdded.delete(variant);
        card.classList.remove("is-added");
        return;
      }

      const added = this.upsellAdded.has(variant);
      card.classList.toggle("is-added", added);

      const button = card.querySelector(".cart-drawer__upsell-button[data-label-added]");
      if (added && button) button.setAttribute("aria-label", button.dataset.labelAdded);
    });

    this.querySelectorAll("[data-upsell]").forEach((block) => {
      const visible = block.querySelector(".cart-drawer__upsell-item:not(.is-in-cart), .cart-drawer__upsell-item.is-added");
      block.classList.toggle("is-empty", !visible);
      this.updateUpsellArrows(block);
    });
  }

  updateUpsellArrows(block) {
    const list = block.querySelector("[data-upsell-list]");
    if (!list) return;

    const max = list.scrollWidth - list.clientWidth;
    block.classList.toggle("is-scrollable", max > 2);

    const prev = block.querySelector('[data-action="upsell-prev"]');
    const next = block.querySelector('[data-action="upsell-next"]');
    if (prev) prev.disabled = list.scrollLeft <= 2;
    if (next) next.disabled = list.scrollLeft >= max - 2;
  }

  onUpsellScroll(event) {
    const list = event.target;
    if (!(list instanceof HTMLElement) || !list.hasAttribute("data-upsell-list")) return;
    if (this.upsellFrame) return;

    this.upsellFrame = requestAnimationFrame(() => {
      this.upsellFrame = null;
      const block = list.closest("[data-upsell]");
      if (block) this.updateUpsellArrows(block);
    });
  }

  // One card per press. The step is read from the cards themselves, so it stays
  // right on both the drawer's widths.
  scrollUpsell(button, direction) {
    const list = button.closest("[data-upsell]")?.querySelector("[data-upsell-list]");
    const card = list?.querySelector(".cart-drawer__upsell-item:not(.is-in-cart), .cart-drawer__upsell-item.is-added");
    if (!list || !card) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    list.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: "smooth" });
  }

  onClickEvent(event) {
    if (event.target.hasAttribute("data-action")) {
      event.preventDefault();

      const item = event.target.closest("[data-item]");
      const itemQuantityInput = item?.querySelector('[name="quantity"]') ?? false;
      const itemQuantity = parseInt(itemQuantityInput?.value) ?? false;
  
      switch (event.target.dataset.action) {
        case "close":
          this.close();
          break;
        case "remove-item":
          if (!this.isLoading) {
            this.removeItem(item);
          }
          break;
        case "decrease-quantity":          
          if (itemQuantity > 1) {
            if (!this.isLoading) {
              this.updateQuantity(item.dataset.key, parseInt(itemQuantity - 1), item);
            }
          }
          break;
        case "increase-quantity":
          if (itemQuantity < 199) {
            if (!this.isLoading) {
              this.updateQuantity(item.dataset.key, parseInt(itemQuantity + 1), item);
            }
          }
          break;
        case "add-product": {
          const card = event.target.closest(".cart-drawer__upsell-item");
          if (!this.isLoading && !card?.classList.contains("is-added")) {
            this.addItemToCart(event.target.dataset.variant, card || event.target);
          }
          break;
        }
        case "upsell-prev":
          this.scrollUpsell(event.target, -1);
          break;
        case "upsell-next":
          this.scrollUpsell(event.target, 1);
          break;
        case "toggle-shipping-insurance":
          if (!this.classList.contains("cart-busy")) {
            this.toggleShippingInsurance(event.target);
          }
          break;
      }
    }
  }

  // A quantity typed into the field. It used to change nothing in the cart while
  // the field kept showing the new number.
  onChangeEvent(event) {
    const input = event.target;
    if (input.name !== "quantity") return;

    const item = input.closest("[data-item]");
    const quantity = Math.min(parseInt(input.value, 10), 199);

    if (!item || this.isLoading || !(quantity >= 0) || quantity === parseInt(input.defaultValue, 10)) {
      input.value = input.defaultValue;
      return;
    }

    // Zero takes the line out, the same way the bin does, gift included.
    if (quantity === 0) {
      this.removeItem(item);
    } else {
      this.updateQuantity(item.dataset.key, quantity, item);
    }
  }

  onSubmitEvent(event) {
    // Enter in a quantity field submitted the item list to /cart and left the page.
    // The change event that comes with it does the update instead.
    if (event.target.id === "CartDrawer-Form") {
      event.preventDefault();
      return;
    }

    if (!event.target.hasAttribute("data-checkout-form")) return;

    // CSS already takes the pointer events off the button while the cart is mid-change;
    // the guard is here too (Enter on a focused button still submits) because leaving
    // for the checkout with a cart request still in flight is the one mistake that
    // costs an order. A second submit while the first is on its way is refused too.
    const button = event.target.querySelector('[name="checkout"]');
    if (this.classList.contains("cart-busy") || button?.classList.contains("loading")) {
      event.preventDefault();
      return;
    }

    button?.classList.add("loading");
  }

  // Back from the checkout the page comes out of the bfcache with the spinner still on,
  // and with the cart as it was before leaving — the order may have been placed or
  // changed since, or the bundle builder may have replaced it. The drawer is
  // re-rendered from the server so the count, the lines and the button are true again.
  // Not only the bfcache: when the browser keeps the page out of it, Back reloads the
  // HTML from its HTTP cache and the drawer comes up with the old cart all the same.
  onPageShow(event) {
    const fromHistory = event.persisted || performance.getEntriesByType?.("navigation")[0]?.type === "back_forward";
    if (!fromHistory) return;

    this.querySelector('[data-checkout-form] .loading')?.classList.remove("loading");
    this.refresh();
  }

  refresh() {
    if (this.isLoading || this.giftBusy || this.insuranceBusy) return;

    fetch(`${window.location.pathname}?sections=cart-drawer`)
    .then(response => response.json())
    .then(sections => {
      // A cart change that started meanwhile renders a newer cart than this one.
      if (this.isLoading || this.giftBusy || this.insuranceBusy) return;
      this.renderContent(sections);
    })
    .catch(error => console.error(error));
  }

  toggleLoading(itemEl) {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading-item", this.isLoading);
    this.setCartBusy();

    if (itemEl) {
      itemEl.classList.toggle("loading", this.isLoading);
    }
  }

  // Free shipping progress — computed in JS so the threshold (shop currency)
  // is converted to the active presentment currency via window.Shopify.currency.rate.
  updateFreeShipping() {
    const bar = this.querySelector("[data-free-shipping]");
    if (!bar) return;

    const rate = parseFloat(window.Shopify?.currency?.rate || 1);
    // Round the converted threshold to a whole currency unit, matching the
    // "round converted prices" rounding rule set in the Shopify admin.
    const threshold = Math.round(((parseInt(bar.dataset.threshold, 10) || 0) * rate) / 100) * 100;
    // The insurance add-on is already subtracted from this total in Liquid: it is a
    // service on the order, and letting it carry the customer over the threshold
    // would be giving away shipping on products they never bought.
    const total = parseInt(this.dataset.freeShippingTotal || this.dataset.cartTotal, 10) || 0;

    const progress = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 100;
    const diff = Math.max(threshold - total, 0);

    const thumb = bar.querySelector('[data-progress="shipping"]');
    if (thumb) thumb.style.width = `${progress}%`;

    const label = bar.querySelector(".cart-drawer__achivements-label");
    if (label) {
      if (diff <= 0) {
        label.innerHTML = bar.dataset.labelReached;
      } else {
        const amount = formatMoney(diff, bar.dataset.moneyFormat);
        label.innerHTML = bar.dataset.labelRemaining.replace("__amount__", amount);
      }
    }

    bar.classList.toggle("reached", diff <= 0);
  }

  // Reserved-cart countdown (10 min). Stored in localStorage so it survives reloads;
  // starts on the first add, resets only when the cart is emptied.
  updateTimer() {
    const el = this.querySelector("[data-cart-timer]");

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    if (!el) return;

    const key = "safecourt:cart-timer";
    const count = parseInt(this.dataset.count, 10) || 0;

    if (count === 0) {
      safeStorage("removeItem", key);
      return;
    }

    let expires = parseInt(safeStorage("getItem", key), 10);
    if (!expires) {
      expires = Date.now() + 10 * 60 * 1000;
      safeStorage("setItem", key, expires);
    }

    const tick = () => {
      const remaining = expires - Date.now();
      if (remaining <= 0) {
        el.textContent = "00:00";
        clearInterval(this.timerInterval);
        this.timerInterval = null;
        return;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      el.textContent = `${minutes}:${seconds}`;
    };

    tick();
    this.timerInterval = setInterval(tick, 1000);
  }

  // The cart page renders this same element as its content instead of an
  // overlay. Everything else is unchanged — the gift reconcile, the insurance
  // add-on, the upsells and the section re-render all work off the same markup
  // — but it has nothing to slide open, and locking the document would freeze
  // the page it is sitting on. Add-to-cart from a card or an upsell still calls
  // open() on whatever cart-drawer it finds, so this has to be a no-op here
  // rather than something the callers have to know about.
  get isPage() {
    return this.classList.contains("cart-drawer--page");
  }

  open() {
    // Nothing to slide open, but the callers still mean "show me the cart":
    // the cart button in the header, and an add from an upsell card. Scrolled
    // away from it, that should bring it back; already looking at it, it must
    // not yank the page while the list updates underneath.
    if (this.isPage) {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      if (rect.bottom < 0 || rect.top > viewport) {
        this.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      return;
    }

    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";

    // The drawer's stylesheet loads late and the drawer was out of view: measure the
    // carousels now that they are on screen.
    this.applyUpsellState();
  }

  close() {
    if (this.isPage) return;
    this.classList.remove("open");
    document.documentElement.style.overflow = null;

    // Next time the drawer opens, products already in the cart are no longer offered.
    this.upsellAdded.clear();
    this.applyUpsellState();
  }
}

if (document.querySelector("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class Localization extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initClickEvents();

    document.addEventListener("click", (e) => {
      if (e.target.hasAttribute("data-localization-open")) {
        this.open();
      }
    })
  }

  initClickEvents() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      const form = this.querySelector("form");
      switch (e.target.dataset.action) {
        case "close":
          form.classList.remove("open");
          this.close();
          break;
        case "toggle-options":
          form.classList.toggle("open");
          break;
        case "select-option":
          const selected = this.querySelector('[data-action="toggle-options"]');
          selected.innerHTML = e.target.innerHTML;
          form.classList.remove("open");
          form.querySelector("[name='country_code']").value = e.target.dataset.value;
          break;
        case "save":
          this.close();
          form.submit();
          break;
      }
    })
  }

  open() {
    this.classList.add("open");
    document.documentElement.style.overflow = "hidden";
  }

  close() {
    this.classList.remove("open");
    document.documentElement.style.overflow = null;
  }
}


if (document.querySelector("localization-popup")) {
  customElements.define("localization-popup", Localization);
}